/**
 * Smart Insights backend response transformers.
 *
 * Backend contract (category-aware):
 *
 * 1. Metric (computation_results item):
 *    { metric: string, value: number, status: "completed", category?: string }
 *    category is used for story grouping (e.g. "Revenue", "Cost").
 *
 * 2. Chart (charts[] item):
 *    { title, type, category?, x_field, y_field, data[], aggregations? }
 *    category is used for story grouping; we also accept group as alias.
 *
 * 3. Optional pre-built groups (summary_ready payload):
 *    groups: [{ category, summary, charts[], metrics[] }] — used as-is when present.
 *
 * Other expected fields: sql_plan, generated_sql, source_data, source_data_metadata,
 * computation_results (with optional operational_plan, category, group). See api.ts types.
 */
import { DashboardResponse, ChartResponse } from '../../../services/api';
import { formatValue } from '../utils/numberFormat';
import { DashboardData, KPIMetric, AIInsight, ChartData, ChartSeriesDef, TableRow, ActionItem, DashboardCategoryGroup } from '../types/dashboard';

// Compatibility step type. The public ``MetricStep`` interface was retired,
// but older persisted ChartResponse payloads still need this local shape.
interface MetricStep {
  id: string;
  label: string;
  value: string | number;
  unit?: string;
  description?: string;
  formula?: string;
  subSteps?: MetricStep[];
}
import { deriveSignalsFromChart, getOverrideChartType } from '../utils/chartSelectionEngine';
import { adjustChartData } from '../utils/chartDataAdjuster';

/** Normalize category for grouping: lowercase, default "other". */
function normalizeCategory(cat: string | undefined | null): string {
  return (cat && String(cat).trim()) ? String(cat).toLowerCase() : 'other';
}

/**
 * Transform backend insights (string[]) to frontend AIInsight format
 * Filters out insights about charts/visualizations to show only summary insights
 */
/**
 * Build a `string[]` of insights from the canonical DashboardResponse fields.
 *
 * No SSE partial in SmartCHG ships `response.insights` directly — the upstream
 * `response_orchestration_node` (which used to populate it in the reference
 * Finance-Agent) was deleted. We derive insights from the fields that ARE
 * populated:
 *   - analysis_summary.summaries_by_group  (per-category prose)
 *   - intelligence_analysis.level_*.summary  (per-level analyst summary)
 *   - intelligence_analysis.level_*.drill_down_insights / root_causes /
 *     recommendations / strategic_insights / early_warnings
 *
 * Returns the original `response.insights` if non-empty; otherwise the
 * synthesized list. Works identically for live responses and restored
 * threads (the persisted JSON has the same shape via `_trim_assistant_payload`
 * on the backend).
 *
 * NOTE: This is the canonical helper used by the Smart Insights dashboard
 * render path.
 */
function deriveInsightsFromResponse(response: DashboardResponse): string[] {
  const baseline = Array.isArray(response.insights)
    ? response.insights.filter(s => typeof s === 'string' && s.trim().length > 0)
    : [];
  if (baseline.length > 0) return baseline;

  const out: string[] = [];
  const seen = new Set<string>();
  const add = (text: unknown): void => {
    if (typeof text !== 'string') return;
    const s = text.trim();
    if (!s || seen.has(s)) return;
    seen.add(s);
    out.push(s);
  };

  // 1. Per-category summaries (most actionable for the user's eye).
  const summariesByGroup = response.analysis_summary?.summaries_by_group;
  if (Array.isArray(summariesByGroup)) {
    summariesByGroup.forEach((g: any) => {
      if (!g || typeof g !== 'object') return;
      const cat = String(g.category ?? g.group ?? g.name ?? '').trim();
      const text = String(g.text ?? g.summary ?? g.content ?? '').trim();
      if (cat && text && cat.toLowerCase() !== 'other') {
        const titled = cat.charAt(0).toUpperCase() + cat.slice(1);
        add(`${titled}: ${text}`);
      } else if (text) {
        add(text);
      }
    });
  } else if (summariesByGroup && typeof summariesByGroup === 'object') {
    Object.entries(summariesByGroup).forEach(([key, val]) => {
      const text = typeof val === 'string'
        ? val
        : String((val as any)?.text ?? (val as any)?.summary ?? '').trim();
      if (key && text && key.toLowerCase() !== 'other') {
        const titled = key.charAt(0).toUpperCase() + key.slice(1);
        add(`${titled}: ${text}`);
      } else if (text) {
        add(text);
      }
    });
  }

  // 2. Intelligence levels — drill-downs, root causes, recommendations,
  // strategic insights, early warnings, plus the per-level summary.
  const intel: any = response.intelligence_analysis;
  if (intel && typeof intel === 'object') {
    const levelKeys = [
      'level_1_descriptive_diagnostic',
      'level_1_descriptive',
      'level_2_predictive',
      'level_2_diagnostic',
      'level_3_prescriptive',
      'level_3_predictive',
      'level_4_prescriptive',
      'level_4_strategic',
      'level_5_strategic',
    ];
    for (const key of levelKeys) {
      const level = intel[key];
      if (!level || typeof level !== 'object') continue;
      add(level.summary);
      const buckets = ['drill_down_insights', 'strategic_insights', 'root_causes', 'early_warnings', 'recommendations'];
      for (const b of buckets) {
        const items = level[b];
        if (!Array.isArray(items)) continue;
        for (const item of items) {
          if (typeof item === 'string') {
            add(item);
          } else if (item && typeof item === 'object') {
            add(item.insight ?? item.summary ?? item.text ?? item.description ?? item.recommendation);
          }
        }
      }
    }
    add(intel.summary);
  }

  // 3. Final fallback: top-level analysis_summary.summary_text. Only used
  // when neither summaries_by_group nor intelligence produced anything.
  if (out.length === 0) {
    add(response.analysis_summary?.summary_text);
  }

  return out;
}

function transformInsights(insights: string[]): AIInsight[] {
  // Filter out insights related to charts/visualizations
  const filteredInsights = insights.filter((insight) => {
    const lowerInsight = insight.toLowerCase();
    // Exclude insights that mention charts, visualizations, or similar terms
    return !(
      lowerInsight.includes('generated') && (
        lowerInsight.includes('visualization') ||
        lowerInsight.includes('chart') ||
        lowerInsight.includes('graph') ||
        lowerInsight.includes('plot')
      )
    );
  });

  return filteredInsights.map((insight, index) => {
    // Try to determine insight type from content
    const lowerInsight = insight.toLowerCase();
    let type: 'warning' | 'success' | 'info' | 'alert' = 'info';

    if (lowerInsight.includes('warning') || lowerInsight.includes('concern') || lowerInsight.includes('issue')) {
      type = 'warning';
    } else if (lowerInsight.includes('success') || lowerInsight.includes('improved') || lowerInsight.includes('positive')) {
      type = 'success';
    } else if (lowerInsight.includes('alert') || lowerInsight.includes('critical') || lowerInsight.includes('urgent')) {
      type = 'alert';
    }

    // Extract title and description from insight text
    const parts = insight.split(':');
    const title = parts.length > 1 ? parts[0].trim() : insight.substring(0, 50) + (insight.length > 50 ? '...' : '');
    const description = parts.length > 1 ? parts.slice(1).join(':').trim() : insight;

    return {
      id: `insight-${index}`,
      type,
      title,
      description: description, // Show full description without truncation
    };
  });
}

const CHART_CATEGORY_FIELD_NAMES = new Set(['name', 'label', 'category', 'date', 'x', 'dimension']);

function firstNonEmptyString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value !== 'string' && typeof value !== 'number') continue;
    const str = String(value).trim();
    if (str) return str;
  }
  return undefined;
}

type StructuredBlock = {
  id?: string;
  type?: string;
  heading?: string | null;
  relevance?: {
    salience?: number | null;
    rationale?: string | null;
  } | null;
  data?: Record<string, any> | null;
  [key: string]: any;
};

type StructuredEnvelope = {
  $schema?: string;
  meta?: Record<string, any>;
  query_context?: Record<string, any>;
  blocks?: StructuredBlock[];
  [key: string]: any;
};

function isRecord(value: unknown): value is Record<string, any> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function asRecord(value: unknown): Record<string, any> {
  return isRecord(value) ? value : {};
}

function asArray(value: unknown): any[] {
  return Array.isArray(value) ? value : [];
}

function periodTitleCandidate(...sources: Array<Record<string, any> | null | undefined>): string | undefined {
  const keys = [
    'period_label',
    'periodLabel',
    'fiscal_period_label',
    'time_period_description',
    'timePeriodDescription',
    'period_used_label',
    'periodUsedLabel',
    'period_used',
    'periodUsed',
    'time_cut',
    'period',
  ];

  for (const source of sources) {
    if (!isRecord(source)) continue;
    for (const key of keys) {
      const value = firstNonEmptyString(source[key]);
      if (value) return formatPeriodTitleLabel(value);
    }
  }
  return undefined;
}

function formatPeriodTitleLabel(value: string): string {
  const label = String(value || '').replace(/\s+/g, ' ').trim();
  if (!label) return '';

  const slashMatch = label.match(/^(.+?)\s*\/\s*(FY\s*\d{4}\s*P\s*0?\d{1,2})$/i);
  if (slashMatch) {
    return `${slashMatch[1].trim()} (${slashMatch[2].replace(/\s+/g, ' ').trim().replace(/\bP\s*0?(\d{1,2})\b/i, 'P$1')})`;
  }

  const isoMonthMatch = label.match(/^(\d{4})-(\d{2})$/);
  if (isoMonthMatch) {
    const date = new Date(Number(isoMonthMatch[1]), Number(isoMonthMatch[2]) - 1, 1);
    return date.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  }

  return label
    .replace(/\bFY\s*(\d{4})\s*period\s*0?(\d{1,2})\b/i, 'FY$1 P$2')
    .replace(/\bFY\s*(\d{4})\s*P\s*0?(\d{1,2})\b/i, 'FY$1 P$2');
}

function titleAlreadyHasPeriod(title: string, periodLabel: string): boolean {
  const normalizedTitle = title.toLowerCase().replace(/\s+/g, ' ');
  const normalizedPeriod = periodLabel.toLowerCase().replace(/\s+/g, ' ');
  if (normalizedTitle.includes(normalizedPeriod)) return true;

  const fyPeriod = periodLabel.match(/\bFY\s*(\d{4})\s*P\s*0?(\d{1,2})\b/i);
  if (fyPeriod) {
    const [, year, period] = fyPeriod;
    const fyPattern = new RegExp(`\\bfy\\s*${year}\\s*p\\s*0?${period}\\b`, 'i');
    if (fyPattern.test(title)) return true;
  }

  return /\s[\u2014-]\s.*\b(?:FY\s*\d{4}|P\s*0?\d{1,2}|MTD|QTD|YTD|WTD|Last|Trailing|Rolling)\b/i.test(title);
}

function withMatrixPeriodTitle(
  title: string,
  chartType: ChartData['type'],
  ...sources: Array<Record<string, any> | null | undefined>
): string {
  if (!['matrix', 'heatmap', 'sensitivity_matrix', 'correlation_matrix'].includes(chartType)) return title;
  const periodLabel = periodTitleCandidate(...sources);
  if (!periodLabel || titleAlreadyHasPeriod(title, periodLabel)) return title;
  return `${title.replace(/\s+[\u2014-]\s*$/, '').trim()} \u2014 ${periodLabel}`;
}

function isStructuredEnvelope(value: unknown): value is StructuredEnvelope {
  return isRecord(value) && Array.isArray(value.blocks);
}

function latestUserContent(messages: unknown): string | undefined {
  if (!Array.isArray(messages)) return undefined;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!isRecord(message) || message.role !== 'user') continue;
    const content = firstNonEmptyString(message.content, message.message, message.text);
    if (content) return content;
  }
  return undefined;
}

function latestAssistantStructuredEnvelope(messages: unknown): StructuredEnvelope | null {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (!isRecord(message) || message.role !== 'assistant') continue;
    if (isStructuredEnvelope(message.structured)) return message.structured;
    if (isStructuredEnvelope(message.data?.structured)) return message.data.structured;
  }
  return null;
}

function coerceStructuredEnvelope(response: unknown): StructuredEnvelope | null {
  if (isStructuredEnvelope(response)) return response;
  if (!isRecord(response)) return null;
  if (isStructuredEnvelope(response.structured)) return response.structured;
  if (isStructuredEnvelope(response.data?.structured)) return response.data.structured;
  if (isStructuredEnvelope(response.data)) return response.data;
  if (Array.isArray(response.key_numbers)) {
    return {
      ...response,
      blocks: [{
        id: 'key_numbers',
        type: 'kpi_strip',
        heading: firstNonEmptyString(response.heading, response.title, 'key_numbers'),
        data: { items: response.key_numbers },
      }],
    };
  }
  return latestAssistantStructuredEnvelope(response.messages);
}

function isKpiBlockType(type: string): boolean {
  return ['kpi_strip', 'key_numbers', 'kpis', 'kpi'].includes(type);
}

function humanizeBlockLabel(value?: string | null): string {
  const text = value?.trim();
  if (!text) return '';
  return text
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
}

function normalizeKpiUnit(unit: unknown): string | undefined {
  const text = firstNonEmptyString(unit);
  if (!text) return undefined;
  if (/^usd$/i.test(text)) return 'USD';
  if (/^(pct|percent|percentage)$/i.test(text)) return '%';
  return text;
}

function normalizeMetricValue(value: unknown): string | number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const normalized = value.trim();
    const numeric = Number(normalized.replace(/,/g, ''));
    return Number.isFinite(numeric) && /^[-+]?\d[\d,]*(\.\d+)?$/.test(normalized) ? numeric : normalized;
  }
  return '--';
}

function formatSignedDelta(value: unknown, unit?: string): string {
  if (value === null || value === undefined || value === '') return '';
  const num = typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
  if (!Number.isFinite(num)) return String(value);
  const sign = num > 0 ? '+' : '';
  const suffix = unit === '%' ? '%' : '';
  return `${sign}${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;
}

function metricTrendFromItem(item: Record<string, any>): KPIMetric['trend'] {
  const tone = String(item.tone ?? item.trend ?? '').toLowerCase();
  if (/(positive|good|up|favorable|green)/.test(tone)) return 'up';
  if (/(negative|bad|down|unfavorable|red|risk)/.test(tone)) return 'down';

  const delta = item.delta_pct ?? item.delta_abs;
  const num = typeof delta === 'number' ? delta : Number(String(delta ?? '').replace(/,/g, ''));
  if (Number.isFinite(num) && num !== 0) return num > 0 ? 'up' : 'down';
  return 'neutral';
}

function iconForKpi(item: Record<string, any>): string {
  const text = [
    item.name,
    item.label,
    item.unit,
    item.source_field,
  ].filter(Boolean).join(' ').toLowerCase();
  if (/\b(cost|expense|spend)\b/.test(text)) return 'dollar';
  if (/\bmargin|rate|pct|percent|%\b/.test(text)) return 'gauge';
  if (/\brevenue|sales|usd|\$\b/.test(text)) return 'dollar';
  return 'trending-up';
}

function blockTitle(block: StructuredBlock, fallback: string): string {
  return firstNonEmptyString(block.heading, block.data?.title, block.data?.label, fallback) || fallback;
}

function blockCategory(block: StructuredBlock, index: number): string {
  return firstNonEmptyString(block.id, block.type && `${block.type}-${index}`, `block-${index}`) || `block-${index}`;
}

function kpiBlockToMetrics(block: StructuredBlock): KPIMetric[] {
  return asArray(block.data?.items).map((item, index) => {
    const record = asRecord(item);
    const label = firstNonEmptyString(record.name, record.label, record.title, `Metric ${index + 1}`) || `Metric ${index + 1}`;
    const unit = normalizeKpiUnit(record.unit);
    const trend = metricTrendFromItem(record);
    const trendValue = formatSignedDelta(record.delta_pct, '%') || formatSignedDelta(record.delta_abs, unit);
    const sourceField = firstNonEmptyString(record.source_field, record.field_citation, record.column);

    return {
      id: firstNonEmptyString(record.id, `${block.id || 'kpi'}-${index}`) || `${block.id || 'kpi'}-${index}`,
      label,
      value: normalizeMetricValue(record.value),
      unit,
      trend,
      trendValue,
      icon: iconForKpi(record),
      category: block.heading || block.type,
      reasoning: firstNonEmptyString(record.rationale, block.relevance?.rationale),
      column: sourceField,
      source_field: sourceField,
      methodology: firstNonEmptyString(record.methodology),
      period: firstNonEmptyString(record.period),
      periodLabel: firstNonEmptyString(record.period_label, record.periodLabel),
      availability: firstNonEmptyString(record.availability),
      status: firstNonEmptyString(record.status),
      cardLayout: firstNonEmptyString(record.card_layout, record.cardLayout),
      comparators: Array.isArray(record.comparators) ? record.comparators : undefined,
      spark: isRecord(record.spark) ? record.spark as KPIMetric['spark'] : undefined,
      salience: typeof record.salience === 'number' ? record.salience : null,
      toolCallId: firstNonEmptyString(record.tool_call_id, record.toolCallId),
      // Compact lineage payload — single source of truth for the
      // LineagePanel. Falls back gracefully when absent (legacy
      // responses persisted before this contract).
      lineage_v2: isRecord(record.lineage_v2)
        ? (record.lineage_v2 as KPIMetric['lineage_v2'])
        : undefined,
    };
  });
}

// Repo-2 branding pass: preserve matrix cells from structured chart JSON for v2 rendering.
function normalizeBlockChartType(value: unknown): ChartData['type'] {
  const chartType = String(value || '').toLowerCase();
  // Order matters — narrower variants first. ``dual_axis_bar_line``
  // must be detected before generic ``dual_axis`` so we don't downgrade
  // combo (bar + line) charts to plain dual_axis_line.
  if (chartType.includes('dual_axis_bar_line') || chartType.includes('bar_line')) return 'dual_axis_bar_line';
  if (chartType.includes('dual_axis')) return 'dual_axis_line';
  if (chartType.includes('matrix')) return 'matrix';
  if (chartType.includes('waterfall')) return 'waterfall';
  if (chartType.includes('horizontal')) return 'horizontal_bar';
  if (chartType.includes('stacked')) return 'stacked_bar';
  if (chartType.includes('multi')) return 'multi_column_bar';
  if (chartType.includes('donut')) return 'donut';
  if (chartType.includes('pie')) return 'pie';
  if (chartType.includes('area')) return 'area';
  if (chartType.includes('line') || chartType.includes('time')) return 'line';
  if (chartType.includes('heat')) return 'heatmap';
  if (chartType.includes('tree')) return 'treemap';
  if (chartType.includes('funnel')) return 'funnel';
  if (chartType.includes('scatter')) return 'scatter';
  if (chartType.includes('bubble')) return 'bubble';
  if (chartType.includes('sparkline') || chartType.includes('kpi_sparkline')) return 'kpi_sparkline';
  if (chartType.includes('radar')) return 'radar';
  if (chartType.includes('parallel')) return 'parallel_coordinates';
  if (chartType.includes('sunburst')) return 'sunburst';
  return 'bar';
}

function isTechnicalDisplayLabel(field: string | undefined, label: string | undefined): boolean {
  if (!field || !label) return false;
  return label === field || label.replace(/\s+/g, '_') === field;
}

function friendlyChartFieldLabel(
  field: string | undefined,
  displayNames?: Record<string, string>,
  fallback?: string,
): string {
  const key = String(field || '').trim();
  const displayLabel = key ? firstNonEmptyString(displayNames?.[key]) : undefined;
  if (displayLabel && !isTechnicalDisplayLabel(key, displayLabel)) return displayLabel;
  return firstNonEmptyString(fallback, key ? humanizeChartKey(key) : undefined, 'Value') || 'Value';
}

function normalizedLabel(label: string | undefined | null): string {
  return String(label || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function sameDisplayLabel(left: string | undefined | null, right: string | undefined | null): boolean {
  const l = normalizedLabel(left);
  const r = normalizedLabel(right);
  return Boolean(l && r && l === r);
}

function inferMatrixRowLabelFromTitle(title: string | undefined): string | undefined {
  const text = String(title || '').trim();
  if (!text) return undefined;
  const byMatch = text.match(/\bby\s+(.+)$/i);
  if (!byMatch) return undefined;
  let label = byMatch[1].trim();
  label = label.split(/\s+[\u2013\u2014-]\s+/)[0].trim();
  label = label.replace(/\s+(?:x|\u00d7)\s+.*$/i, '').trim();
  label = label.replace(/\s+and\s+Fiscal\s+(?:Period|Quarter|Year|Month|Week)\b.*$/i, '').trim();
  return label || undefined;
}

function inferMatrixMeasureLabelsFromTitle(title: string | undefined): { valueLabel?: string; secondaryLabel?: string } {
  const text = String(title || '').trim();
  if (!text) return {};
  const measurePart = text.split(/\s+\bby\b\s+/i)[0]?.trim();
  if (!measurePart) return {};
  const parts = measurePart
    .split(/\s+(?:and|vs\.?)\s+|\s*&\s*/i)
    .map((part) => part.replace(/\s+[\u2013\u2014-].*$/, '').trim())
    .filter(Boolean);
  const valueLabel = parts[0];
  const secondaryLabel = parts[1] && !sameDisplayLabel(parts[0], parts[1]) ? parts[1] : undefined;
  return { valueLabel, secondaryLabel };
}

function resolveMatrixDisplayLabels(
  data: Record<string, any>,
  displayNames: Record<string, string>,
): {
  rowLabel: string;
  colLabel: string;
  valueLabel: string;
  secondaryLabel?: string;
} {
  const title = firstNonEmptyString(data.title, data.label);
  const inferredRowLabel = inferMatrixRowLabelFromTitle(title);
  const inferredMeasureLabels = inferMatrixMeasureLabelsFromTitle(title);
  const rowDim = firstNonEmptyString(data.matrix_row_dim, data.matrixRowDim);
  const colDim = firstNonEmptyString(data.matrix_col_dim, data.matrixColDim, data.x_field, data.xField);
  const primaryMeasure = firstNonEmptyString(data.matrix_value_measure, data.matrixValueMeasure);
  const secondaryMeasure = firstNonEmptyString(data.matrix_secondary_measure, data.matrixSecondaryMeasure);

  const colLabel = friendlyChartFieldLabel(colDim, displayNames, displayNames.column || displayNames.Column || 'Column');
  let rowLabel = friendlyChartFieldLabel(
    rowDim,
    displayNames,
    inferredRowLabel || displayNames.name || displayNames.Name || 'Name',
  );
  if (!rowDim && inferredRowLabel) rowLabel = inferredRowLabel;
  if (sameDisplayLabel(rowLabel, colLabel) && inferredRowLabel) rowLabel = inferredRowLabel;

  const valueLabel = primaryMeasure
    ? friendlyChartFieldLabel(
      primaryMeasure,
      displayNames,
      inferredMeasureLabels.valueLabel || displayNames.value || displayNames.Value || 'Value',
    )
    : firstNonEmptyString(inferredMeasureLabels.valueLabel, displayNames.value, displayNames.Value, 'Value') || 'Value';

  const secondaryCandidate = secondaryMeasure
    ? friendlyChartFieldLabel(
      secondaryMeasure,
      displayNames,
      inferredMeasureLabels.secondaryLabel || displayNames.secondary_value || displayNames['Secondary Value'] || 'Secondary Value',
    )
    : firstNonEmptyString(inferredMeasureLabels.secondaryLabel, displayNames.secondary_value, displayNames['Secondary Value']);
  const secondaryLabel = sameDisplayLabel(valueLabel, secondaryCandidate) ? undefined : secondaryCandidate;

  return { rowLabel, colLabel, valueLabel, secondaryLabel };
}

function chartCellsFromBlockData(data: Record<string, any>): ChartData['cells'] {
  const directCells = asArray(data.cells).filter(isRecord);
  const series = asArray(data.series).filter(isRecord);
  const seriesCells = series.flatMap((item) => asArray(item.data).filter(isRecord));
  const candidates = directCells.length > 0 ? directCells : seriesCells;

  return candidates.map((cell) => {
    const row = firstNonEmptyString(cell.row, cell.y, cell.name, cell.category, cell.dimension) || 'Row';
    const col = firstNonEmptyString(cell.col, cell.column, cell.x, cell.measure, cell.series) || 'Value';
    const rawValue = cell.value ?? cell.amount ?? cell.count;
    const rawSecondaryValue = cell.secondary_value ?? cell.secondaryValue;
    return {
      row,
      col,
      value: rawValue == null || rawValue === '' ? null : toChartNumber(rawValue),
      secondary_value: rawSecondaryValue == null || rawSecondaryValue === '' ? null : toChartNumber(rawSecondaryValue),
      tone: firstNonEmptyString(cell.tone, cell.status),
      raw: cell,
    };
  });
}

function tableRowsToChartRows(table: unknown): any[] {
  const record = asRecord(table);
  const columns = asArray(record.columns).map((column) => String(column ?? '').trim()).filter(Boolean);
  const rows = asArray(record.rows);
  if (columns.length === 0 || rows.length === 0) return [];

  return rows.map((row, index) => {
    const cells = Array.isArray(row) ? row : [];
    const name = firstNonEmptyString(cells[0], `Row ${index + 1}`) || `Row ${index + 1}`;
    if (columns.length <= 2) {
      return { name, value: toChartNumber(cells[1]) };
    }

    const out: Record<string, any> = { name };
    columns.slice(1).forEach((column, columnIndex) => {
      const value = cells[columnIndex + 1];
      out[column] = isChartNumericValue(value) ? toChartNumber(value) : value;
    });
    return out;
  });
}

function chartRowsFromBlockData(data: Record<string, any>): any[] {
  const matrixCells = chartCellsFromBlockData(data);
  if (matrixCells.length > 0) {
    const displayNames = isRecord(data.metric_display_names)
      ? data.metric_display_names as Record<string, string>
      : {};
    const { rowLabel, colLabel, valueLabel, secondaryLabel } = resolveMatrixDisplayLabels(data, displayNames);
    const includeSecondary = Boolean(secondaryLabel)
      && matrixCells.some((cell) => cell.secondary_value !== null && cell.secondary_value !== undefined);

    return matrixCells.map((cell) => ({
      [rowLabel]: cell.row,
      [colLabel]: cell.col,
      [valueLabel]: cell.value,
      ...(includeSecondary ? { [secondaryLabel || 'Secondary Value']: cell.secondary_value ?? null } : {}),
    }));
  }

  const directRows = asArray(data.data);
  if (directRows.length > 0) {
    // Canonical orchestrator-V2 chart spec carries explicit ``x_field``
    // + per-measure columns (e.g. Prod_Class_Mat_Sales_Comp_T +
    // Net_Sales_USD + Variable_Margin_Pct). When that's the case we
    // MUST NOT synthesize ``name`` / ``value`` keys onto the rows:
    //   * the chart renderer reads ``chart.series[*].field`` +
    //     ``getCategoryValue`` (which falls back to the first
    //     non-numeric key) so it doesn't need them;
    //   * the expand-modal data table renders every row key as a
    //     column — synthetic ``name`` surfaces as a duplicate dim
    //     column with ``Item 1, Item 2, …`` placeholder values, and
    //     synthetic ``value`` surfaces as a phantom "Value" column
    //     with no schema/lineage entry.
    // The legacy fallback (no ``x_field`` on the block) is preserved
    // below — older fixtures + third-party block payloads still get
    // ``name`` / ``value`` synthesized so the older renderers can
    // pick them up.
    const xField = typeof data.x_field === 'string' ? data.x_field : '';
    return directRows.map((row, index) => {
      if (!isRecord(row)) {
        return {
          name: `Item ${index + 1}`,
          value: toChartNumber(row),
        };
      }
      if (xField && row[xField] !== undefined && row[xField] !== null) {
        return { ...row };
      }
      const numericValue = row.value ?? row.amount ?? row.count ?? Object.values(row).find(isChartNumericValue);
      return {
        ...row,
        name: firstNonEmptyString(row.name, row.label, row.category, row.x, row.dimension, `Item ${index + 1}`) || `Item ${index + 1}`,
        value: row.value !== undefined ? row.value : toChartNumber(numericValue),
      };
    });
  }

  const categories = asArray(data.axes?.x?.categories).map((category, index) => (
    firstNonEmptyString(category, `Item ${index + 1}`) || `Item ${index + 1}`
  ));
  const series = asArray(data.series).filter(isRecord);
  if (series.length > 0) {
    const maxLength = Math.max(
      categories.length,
      ...series.map((item) => Array.isArray(item.data) ? item.data.length : 0)
    );
    const fallbackCategories = Array.from({ length: maxLength }, (_value, index) => categories[index] || `Item ${index + 1}`);

    if (series.length === 1) {
      const values = asArray(series[0].data);
      return fallbackCategories.map((category, index) => ({
        name: category,
        value: toChartNumber(values[index]),
      }));
    }

    return fallbackCategories.map((category, index) => {
      const row: Record<string, any> = { name: category };
      series.forEach((item, seriesIndex) => {
        const name = firstNonEmptyString(item.name, `Series ${seriesIndex + 1}`) || `Series ${seriesIndex + 1}`;
        row[name] = toChartNumber(asArray(item.data)[index]);
      });
      return row;
    });
  }

  return tableRowsToChartRows(data.fallback_table);
}

function chartBlockToChart(
  block: StructuredBlock,
  index: number,
  context?: {
    response?: Record<string, any>;
    envelope?: StructuredEnvelope;
  },
): ChartData {
  const data = asRecord(block.data);
  const cells = chartCellsFromBlockData(data);
  const rows = chartRowsFromBlockData(data);
  const type = normalizeBlockChartType(firstNonEmptyString(data.chart_type, data.type, asArray(data.series)[0]?.render_hint));
  const numericFields = getChartNumericFields(rows, 'name').filter(key => key !== 'value');
  const series = asArray(data.series).filter(isRecord);
  const backendDisplayNames = isRecord(data.metric_display_names)
    ? data.metric_display_names as Record<string, string>
    : {};
  const displayNames: Record<string, string> = { ...backendDisplayNames };
  const xField = firstNonEmptyString(data.x_field, data.xField);
  const xLabel = firstNonEmptyString(data.axes?.x?.label);
  const yLabel = firstNonEmptyString(data.axes?.y?.label, data.axes?.y?.format);
  if (xField) {
    const friendlyX = friendlyChartFieldLabel(xField, displayNames, xLabel);
    displayNames[xField] = friendlyX;
    if (!displayNames.name) displayNames.name = friendlyX;
    if (!displayNames.Name) displayNames.Name = friendlyX;
  } else if (xLabel && !displayNames.name) {
    displayNames.name = xLabel;
  }
  // Combo-chart-aware display name extraction. Backend ChartSeriesConfig
  // shape carries {field, label, type, axis} — register `field -> label`
  // so tooltips/legend on dual_axis_bar_line render the human label
  // (e.g. "Net Sales (USD)") instead of the raw column key. Falls back
  // to legacy {name, data[]} series envelopes (older fixtures) where
  // ``name`` carries the display label directly.
  if (series.length === 1 && firstNonEmptyString(series[0].name)) displayNames.value = firstNonEmptyString(series[0].name) as string;
  series.forEach((item) => {
    const fieldKey = firstNonEmptyString(item.field);
    const labelText = firstNonEmptyString(item.label, item.name);
    if (fieldKey && labelText) displayNames[fieldKey] = labelText;
    const legacyName = firstNonEmptyString(item.name);
    if (legacyName && !fieldKey) displayNames[legacyName] = legacyName;
  });

  // Per-series config — forward the BE ChartSeriesConfig shape so the
  // FE renderer can route each measure to the correct axis and marker
  // type. ``dual_axis_bar_line`` reads this; ``DualAxisLineChart`` falls
  // back to a numeric-field-half-split when ``series[]`` is absent.
  const seriesField: ChartSeriesDef[] | undefined = series.length > 0
    ? series.map((item) => {
        const fieldKey = firstNonEmptyString(item.field, item.name) || '';
        const labelText = firstNonEmptyString(item.label, item.name) || fieldKey;
        const seriesTypeRaw = String(item.type || '').toLowerCase();
        const seriesType: ChartSeriesDef['type'] =
          seriesTypeRaw === 'bar' ? 'bar'
          : seriesTypeRaw === 'area' ? 'area'
          : 'line';
        const axisRaw = String(item.axis || '').toLowerCase();
        const axis: ChartSeriesDef['axis'] = axisRaw === 'secondary' ? 'secondary' : 'primary';
        return {
          field: fieldKey,
          label: labelText,
          type: seriesType,
          axis,
          color: typeof item.color === 'string' ? item.color : null,
          unit: typeof item.unit === 'string' ? item.unit : undefined,
          unit_symbol: typeof item.unit_symbol === 'string' ? item.unit_symbol : undefined,
          format: typeof item.format === 'string' ? item.format : undefined,
          lineage_id: typeof item.lineage_id === 'string' ? item.lineage_id : null,
        };
      }).filter((s) => s.field)
    : undefined;
  const secondaryYField: string[] | undefined = Array.isArray(data.secondary_y)
    ? data.secondary_y.filter((v: unknown): v is string => typeof v === 'string' && v.trim().length > 0)
    : undefined;
  const groupByField: string | null | undefined = typeof data.group_by === 'string'
    ? data.group_by
    : data.group_by === null ? null : undefined;
  if (groupByField) {
    displayNames[groupByField] = friendlyChartFieldLabel(groupByField, displayNames);
  }
  if (cells.length > 0) {
    const rowDim = firstNonEmptyString(data.matrix_row_dim, data.matrixRowDim);
    const colDim = firstNonEmptyString(data.matrix_col_dim, data.matrixColDim, data.x_field, data.xField);
    const primaryMeasure = firstNonEmptyString(data.matrix_value_measure, data.matrixValueMeasure);
    const secondaryMeasure = firstNonEmptyString(data.matrix_secondary_measure, data.matrixSecondaryMeasure);
    const { rowLabel, colLabel, valueLabel, secondaryLabel } = resolveMatrixDisplayLabels(data, displayNames);
    displayNames.Name = rowLabel;
    displayNames.name = rowLabel;
    displayNames.Column = colLabel;
    displayNames.column = colLabel;
    displayNames.Value = valueLabel;
    displayNames.value = valueLabel;
    if (secondaryMeasure && secondaryLabel) {
      displayNames['Secondary Value'] = secondaryLabel;
      displayNames.secondary_value = secondaryLabel;
    }
    if (rowDim) displayNames[rowDim] = rowLabel;
    if (colDim) displayNames[colDim] = colLabel;
    if (primaryMeasure) displayNames[primaryMeasure] = valueLabel;
    if (secondaryMeasure && secondaryLabel) displayNames[secondaryMeasure] = secondaryLabel;
  }
  const metricUnitsField = isRecord(data.metric_units) ? (data.metric_units as Record<string, string>) : undefined;
  const metricUnitSymbolsField = isRecord(data.metric_unit_symbols) ? (data.metric_unit_symbols as Record<string, string>) : undefined;
  const metricFormatsField = isRecord(data.metric_formats) ? (data.metric_formats as Record<string, string>) : undefined;

  const rawChartTitle = firstNonEmptyString(data.title, data.label, `Chart ${index + 1}`) || `Chart ${index + 1}`;
  const chartTitle = withMatrixPeriodTitle(
    rawChartTitle,
    type,
    data,
    data.metadata,
    block,
    context?.envelope?.query_context,
    context?.envelope?.meta,
    context?.response?.metadata,
    context?.response?.analysis_summary,
  );

  return {
    // Stable backend-assigned id used by the FE to key chart cards
    // across streaming updates. Without this, chart cards remount
    // every time a new chart_ready frame lands and any open
    // popup / modal / drilldown collapses.
    graph_id: firstNonEmptyString(data.graph_id, data.id, block.id),
    type,
    title: chartTitle,
    data: rows,
    y_field: Array.isArray(data.y_field) ? data.y_field : firstNonEmptyString(data.y_field) ? [firstNonEmptyString(data.y_field) as string] : undefined,
    sort_by: firstNonEmptyString(data.sort_by),
    sort_order: firstNonEmptyString(data.sort_order),
    isMultiSeries: series.length > 1 || numericFields.length > 1,
    x_axis_label: xLabel,
    y_axis_label: yLabel,
    metric_display_names: Object.keys(displayNames).length > 0 ? displayNames : undefined,
    metric_units: metricUnitsField,
    metric_unit_symbols: metricUnitSymbolsField,
    metric_formats: metricFormatsField,
    series: seriesField,
    secondary_y: secondaryYField,
    group_by: groupByField,
    axes: isRecord(data.axes) ? data.axes : undefined,
    matrix_value_measure: firstNonEmptyString(data.matrix_value_measure, data.matrixValueMeasure),
    matrix_secondary_measure: firstNonEmptyString(data.matrix_secondary_measure, data.matrixSecondaryMeasure),
    cells: cells.length > 0 ? cells : undefined,
    dataLimiting: isRecord(data.dataLimiting) ? (data.dataLimiting as ChartData['dataLimiting']) : undefined,
    fullData: Array.isArray(data.fullData) ? data.fullData : undefined,
    caption: firstNonEmptyString(data.caption),
    // Compact lineage payload — single source of truth.
    lineage_v2: isRecord(data.lineage_v2)
      ? (data.lineage_v2 as ChartData['lineage_v2'])
      : undefined,
    reasoning: firstNonEmptyString(block.relevance?.rationale, data.caption),
  } as ChartData;
}

function markdownTable(columns: unknown, rows: unknown): string {
  const safeColumns = asArray(columns).map((column) => String(column ?? '').trim()).filter(Boolean);
  const safeRows = asArray(rows).filter(Array.isArray);
  if (safeColumns.length === 0 || safeRows.length === 0) return '';
  const esc = (value: unknown) => String(value ?? '--').replace(/\|/g, '\\|');
  const header = `| ${safeColumns.map(esc).join(' | ')} |`;
  const divider = `| ${safeColumns.map(() => '---').join(' | ')} |`;
  const body = safeRows.map((row: any[]) => (
    `| ${safeColumns.map((_column, index) => esc(row[index])).join(' | ')} |`
  ));
  return [header, divider, ...body].join('\n');
}

function compactRecordMarkdown(record: Record<string, any>): string {
  const lines: string[] = [];
  Object.entries(record).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    const label = humanizeBlockLabel(key);
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      const text = value.map((item) => (
        isRecord(item) ? firstNonEmptyString(item.summary, item.text, item.point, item.label, item.title) : firstNonEmptyString(item)
      )).filter(Boolean).join(', ');
      if (text) lines.push(`- **${label}:** ${text}`);
      return;
    }
    if (isRecord(value)) {
      const nested = compactRecordMarkdown(value).replace(/^- /gm, '').replace(/\n/g, '; ');
      if (nested) lines.push(`- **${label}:** ${nested}`);
      return;
    }
    lines.push(`- **${label}:** ${String(value)}`);
  });
  return lines.join('\n');
}

function summaryFromLensBlock(block: StructuredBlock): string {
  // The lens / performance_lens block can carry either:
  //   * the legacy shape ``data.items[]`` (older flow), OR
  //   * the orchestrator V2 shape ``data.{strengths, watchouts, risks}``
  //     where each bucket is an array of LensItem records.
  // Walk both — concatenate items from all three buckets when present so
  // ``structuredBlockToGroup`` doesn't drop the section for an empty
  // summary string.
  const data = asRecord(block.data);
  const flat = asArray(data.items);
  const buckets = [
    ...asArray(data.strengths),
    ...asArray(data.watchouts),
    ...asArray(data.risks),
    ...asArray(data.top_risks),
  ];
  const items = flat.length > 0 ? flat : buckets;
  const lines = items.map((item) => {
    const record = asRecord(item);
    const point = firstNonEmptyString(
      record.point, record.headline, record.summary,
      record.text, record.description, record.title,
    );
    if (!point) return '';
    const details = [
      firstNonEmptyString(record.usd_value, record.usd_at_stake) ? `Value: ${firstNonEmptyString(record.usd_value, record.usd_at_stake)}` : '',
      firstNonEmptyString(record.plan) ? `Plan: ${firstNonEmptyString(record.plan)}` : '',
      firstNonEmptyString(record.field_citation) ? `Source: ${firstNonEmptyString(record.field_citation)}` : '',
    ].filter(Boolean);
    return `- ${point}${details.length ? ` (${details.join('; ')})` : ''}`;
  }).filter(Boolean);
  return lines.join('\n');
}

function summaryFromValueAtStakeBlock(block: StructuredBlock): string {
  const data = asRecord(block.data);
  const lines: string[] = [];
  const totalLow = data.total_low_usd;
  const totalHigh = data.total_high_usd;
  if (totalLow !== null && totalLow !== undefined && totalHigh !== null && totalHigh !== undefined) {
    lines.push(`**Total value at stake:** ${formatSignedDelta(totalLow, 'USD')} to ${formatSignedDelta(totalHigh, 'USD')}`);
  }
  asArray(data.items).forEach((item) => {
    const record = asRecord(item);
    const lever = firstNonEmptyString(record.lever, record.title, record.name, 'Opportunity') || 'Opportunity';
    const low = record.low_usd;
    const high = record.high_usd;
    const range = low !== null && low !== undefined && high !== null && high !== undefined
      ? `: ${formatSignedDelta(low, 'USD')} to ${formatSignedDelta(high, 'USD')}`
      : '';
    const details = [
      firstNonEmptyString(record.horizon) ? `Horizon: ${firstNonEmptyString(record.horizon)}` : '',
      firstNonEmptyString(record.method) ? `Method: ${firstNonEmptyString(record.method)}` : '',
      firstNonEmptyString(record.inputs_summary),
    ].filter(Boolean);
    lines.push(`- **${lever}${range}**${details.length ? ` - ${details.join('; ')}` : ''}`);
  });
  return lines.join('\n');
}

function actionsFromNextActionsBlock(block: StructuredBlock): ActionItem[] {
  return asArray(block.data?.actions).map((action, index) => {
    const record = asRecord(action);
    const title = firstNonEmptyString(record.label, record.query, record.title, `Action ${index + 1}`) || `Action ${index + 1}`;
    const query = firstNonEmptyString(record.query);
    return {
      id: firstNonEmptyString(record.id, `${block.id || 'action'}-${index}`) || `${block.id || 'action'}-${index}`,
      priority: 'medium',
      title,
      description: firstNonEmptyString(record.why, record.query, title) || title,
      query,
    };
  });
}

function summaryFromNextActionsBlock(block: StructuredBlock): string {
  return actionsFromNextActionsBlock(block)
    .map((action, index) => `${index + 1}. ${action.title}${action.description && action.description !== action.title ? ` - ${action.description}` : ''}`)
    .join('\n');
}

function summaryFromReasoningTraceBlock(block: StructuredBlock): string {
  return compactRecordMarkdown(asRecord(block.data));
}

function summaryFromActionableInsightsBlock(block: StructuredBlock): string {
  // Markdown summary used by the chat-side feed for the
  // actionable_insights block. The dedicated ActionableInsightsSection
  // component renders the canvas-side cards; this fallback exists so
  // the chat panel and any non-canvas surface still see something
  // readable when the block is forwarded there.
  const data = asRecord(block.data);
  const items = asArray(data.items);
  if (items.length === 0) return '';
  const PRIORITY_LABEL: Record<string, string> = {
    high: 'HIGH',
    medium: 'MEDIUM',
    low: 'LOW',
  };
  const lines: string[] = [];
  items.slice(0, 6).forEach((ai) => {
    const record = asRecord(ai);
    const title = firstNonEmptyString(record.title) || 'Recommendation';
    const priority = String(record.priority || '').toLowerCase();
    const priorityLabel = PRIORITY_LABEL[priority] || 'MEDIUM';
    const action = firstNonEmptyString(record.recommended_action);
    const why = firstNonEmptyString(record.root_cause);
    const impact = firstNonEmptyString(record.estimated_business_impact);
    lines.push(`- **[${priorityLabel}] ${title}**`);
    if (action) lines.push(`  - *Action:* ${action}`);
    if (why) lines.push(`  - *Why:* ${why}`);
    if (impact) lines.push(`  - *Impact:* ${impact}`);
  });
  return lines.join('\n');
}


function summaryFromInsightsBlock(block: StructuredBlock): string {
  // Render lane-titled CFO-grade prose paragraphs (HEADLINE /
  // CONCENTRATION / VARIANCE DRIVERS / TREND / CROSS DIM) using the
  // curator's analyst prose. Empty lanes are skipped so a sparse
  // result doesn't render a wall of empty section headers.
  //
  // Fallback: when the curator didn't emit prose (older payload or
  // analyzer-only run), revert to the prior bulleted "lane — seed"
  // render so the chat surface still shows something.
  const data = asRecord(block.data);
  const headline = firstNonEmptyString(data.headline);
  const concentrationProse    = firstNonEmptyString(data.concentration_prose);
  const varianceDriversProse  = firstNonEmptyString(data.variance_drivers_prose);
  const trendProse            = firstNonEmptyString(data.trend_prose);
  const crossDimProse         = firstNonEmptyString(data.cross_dim_prose);

  const hasProse = !!(
    concentrationProse || varianceDriversProse || trendProse || crossDimProse
  );

  if (hasProse || headline) {
    const sections: string[] = [];
    if (headline) {
      sections.push(`**HEADLINE**\n\n${headline}`);
    }
    if (concentrationProse) {
      sections.push(`**CONCENTRATION**\n\n${concentrationProse}`);
    }
    if (varianceDriversProse) {
      sections.push(`**VARIANCE DRIVERS**\n\n${varianceDriversProse}`);
    }
    if (trendProse) {
      sections.push(`**TREND**\n\n${trendProse}`);
    }
    if (crossDimProse) {
      sections.push(`**CROSS DIM**\n\n${crossDimProse}`);
    }
    if (sections.length > 0) return sections.join('\n\n');
  }

  // Fallback — bulleted detector-finding render.
  const lines: string[] = [];
  if (headline) lines.push(headline);

  const lanes: Array<{ key: string; label: string }> = [
    { key: 'concentration',     label: 'Concentration' },
    { key: 'variance_drivers',  label: 'Variance driver' },
    { key: 'trend',             label: 'Trend' },
    { key: 'cross_dim',         label: 'Cross-dimension' },
  ];

  let bulletCount = 0;
  const MAX_BULLETS = 6;
  lanes.forEach(({ key, label }) => {
    if (bulletCount >= MAX_BULLETS) return;
    asArray(data[key]).forEach((item) => {
      if (bulletCount >= MAX_BULLETS) return;
      const record = asRecord(item);
      const text = firstNonEmptyString(
        record.summary, record.summary_seed, record.text,
        record.point, record.title,
      );
      if (!text) return;
      lines.push(`- **${label}** — ${text}`);
      bulletCount += 1;
    });
  });

  return lines.join('\n\n');
}

function summaryFromStructuredBlock(block: StructuredBlock): string {
  const data = asRecord(block.data);
  const type = String(block.type || '').toLowerCase();
  if (type === 'headline') return firstNonEmptyString(data.text, data.body, data.summary, data.title) || '';
  if (type === 'executive_summary') return firstNonEmptyString(data.text, data.body, data.summary) || '';
  if (type === 'narrative' || type === 'markdown') return firstNonEmptyString(data.body, data.text, data.summary, data.markdown) || '';
  // Chart and KPI-strip blocks render via dedicated components
  // (``ChartSection`` / ``KPIGrid``) — their inline summary should be
  // EMPTY. Without this guard the default branch below falls into
  // ``compactRecordMarkdown(data)`` which dumps every field on the
  // block (chart_type, axes, group_by, chart_metadata, calculation_info,
  // source_data, selected_tables, dataLimiting, …) as a bulleted
  // markdown blob ABOVE the chart canvas — exactly what the user
  // doesn't want on the main feed. The full detail still lives on
  // the block's ``data`` object for the lineage drawer / expand modal
  // to consume.
  if (type === 'chart' || type === 'kpi_strip' || type === 'kpis' || type === 'kpi') return '';
  if (type === 'lens' || type === 'performance_lens') return summaryFromLensBlock(block);
  if (type === 'insights') return summaryFromInsightsBlock(block);
  if (type === 'actionable_insights') return summaryFromActionableInsightsBlock(block);
  if (type === 'value_at_stake') return summaryFromValueAtStakeBlock(block);
  if (type === 'next_actions') return summaryFromNextActionsBlock(block);
  if (type === 'reasoning_trace' || type === 'methodology' || type === 'data_gaps' || type === 'reconciliation') {
    return summaryFromReasoningTraceBlock(block);
  }
  if (type === 'table' || type === 'comparison_table') {
    return markdownTable(data.columns, data.rows) || markdownTable(data.fallback_table?.columns, data.fallback_table?.rows);
  }
  return firstNonEmptyString(data.body, data.text, data.summary, data.description, data.caption) || compactRecordMarkdown(data);
}

function comparableSummaryText(value?: string | null): string {
  return String(value || '')
    .replace(/^[\s>*-]+/, '')
    .replace(/\*\*/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function summaryDuplicatesCaption(summary: string, caption?: string): boolean {
  const normalizedSummary = comparableSummaryText(summary);
  const normalizedCaption = comparableSummaryText(caption);
  if (!normalizedSummary || !normalizedCaption) return false;
  return normalizedSummary === normalizedCaption || normalizedSummary === `caption: ${normalizedCaption}`;
}

function structuredBlockToGroup(
  block: StructuredBlock,
  index: number,
  metrics: KPIMetric[],
  charts: ChartData[],
  summary: string,
): DashboardCategoryGroup | null {
  const description = firstNonEmptyString(block.data?.caption);
  const summaryContent = summaryDuplicatesCaption(summary, description) ? '' : summary;
  if (metrics.length === 0 && charts.length === 0 && !summaryContent.trim()) return null;
  const type = String(block.type || 'section');
  return {
    category: blockCategory(block, index),
    title: blockTitle(block, humanizeBlockLabel(type) || `Section ${index + 1}`),
    description,
    summary: summaryContent,
    metrics,
    charts,
    blockType: type,
    structuredData: asRecord(block.data),
  };
}

function transformStructuredBlocksResponse(response: DashboardResponse): DashboardData | null {
  const envelope = coerceStructuredEnvelope(response);
  if (!envelope) return null;

  const blocks = asArray(envelope.blocks).filter(isRecord) as StructuredBlock[];
  const query = firstNonEmptyString(
    (response as any).query,
    envelope.query,
    envelope.meta?.raw_question,
    latestUserContent((response as any).messages),
    (response as any).thread?.title
  ) || '';

  const metrics: KPIMetric[] = [];
  const charts: ChartData[] = [];
  const groups: DashboardCategoryGroup[] = [];
  const actionItems: ActionItem[] = [];
  const insightItems: AIInsight[] = [];
  const summaryParts: string[] = [];

  blocks.forEach((block, index) => {
    const type = String(block.type || '').toLowerCase();
    let blockMetrics: KPIMetric[] = [];
    let blockCharts: ChartData[] = [];
    const summary = summaryFromStructuredBlock(block);

    if (isKpiBlockType(type)) {
      blockMetrics = kpiBlockToMetrics(block);
      metrics.push(...blockMetrics);
    } else if (type === 'chart') {
      const chart = chartBlockToChart(block, index, { response: response as any, envelope });
      blockCharts = chart.data.length > 0 ? [chart] : [];
      charts.push(...blockCharts);
    } else if (type === 'next_actions') {
      actionItems.push(...actionsFromNextActionsBlock(block));
    } else if (type === 'lens') {
      asArray(block.data?.items).forEach((item, itemIndex) => {
        const record = asRecord(item);
        const description = firstNonEmptyString(record.point, record.summary, record.text, record.description);
        if (!description) return;
        insightItems.push({
          id: firstNonEmptyString(record.id, `${block.id || 'lens'}-${itemIndex}`) || `${block.id || 'lens'}-${itemIndex}`,
          type: String(block.data?.kind || '').toLowerCase().includes('risk') ? 'warning' : 'info',
          title: blockTitle(block, humanizeBlockLabel(block.data?.kind) || 'Insight'),
          description,
        });
      });
    }

    if (type === 'headline' || type === 'narrative' || type === 'executive_summary') {
      if (summary) summaryParts.push(summary);
    }

    // KPI blocks don't become groups (they emit through ``metrics``);
    // executive_summary doesn't become a group either — it renders
    // exclusively as the top-of-canvas ``DashboardSummaryBlock`` via
    // ``data.summaryText`` (set from ``summaryParts``). Letting it
    // become a group would emit a duplicate "Summary" tile under the
    // top block.
    const group = (isKpiBlockType(type) || type === 'executive_summary')
      ? null
      : structuredBlockToGroup(block, index, blockMetrics, blockCharts, summary);
    if (group) groups.push(group);
  });

  const headline = blocks.find((block) => String(block.type || '').toLowerCase() === 'headline');
  const title = firstNonEmptyString(
    (response as any).title,
    envelope.title,
    (response as any).task,
    envelope.task,
    headline?.heading,
  );
  const subtitle = firstNonEmptyString((response as any).subtitle, (response as any).description);
  const summaryText = summaryParts.filter(Boolean).join('\n\n') || firstNonEmptyString((response as any).summary, (response as any).normal_text);

  // ``suggested_queries`` arrives on the response from the
  // InsightBuilder's ``follow_up_questions`` node (also via the
  // streaming envelope's ``suggested_queries`` array). Surface them
  // on ``DashboardData`` so the canvas footer can render the chips.
  const suggestedQueries = Array.isArray((response as any).suggested_queries)
    ? (response as any).suggested_queries.filter((q: any): q is string => typeof q === 'string' && q.trim().length > 0)
    : [];

  return {
    query,
    title,
    subtitle,
    metrics,
    insights: insightItems,
    charts,
    tableData: [],
    actionItems,
    intelligenceAnalysis: (response as any).intelligence_analysis,
    botMessageId: (response as any).botMessageId || (response as any).bot_message_id,
    summaryText,
    suggestedQueries,
    analysisSummary: {
      ...(isRecord((response as any).analysis_summary) ? (response as any).analysis_summary : {}),
      meta: envelope.meta,
      query_context: envelope.query_context,
      schema: envelope.$schema,
    },
    groups: groups.length > 0 ? groups : undefined,
  };
}

function getRawChartType(chart: ChartResponse): string {
  return firstNonEmptyString((chart.spec as any)?.chartType, (chart.spec as any)?.type, chart.chart_type)?.toLowerCase() || '';
}

function getChartXField(chart: ChartResponse): string | undefined {
  return firstNonEmptyString((chart.spec as any)?.xField, chart.x_field, chart.data_key);
}

function getChartYField(chart: ChartResponse): string | undefined {
  return getChartYFields(chart)[0];
}

function getChartYFields(chart: ChartResponse): string[] {
  const raw = (chart.spec as any)?.yField ?? chart.y_field;
  if (Array.isArray(raw)) {
    return raw.filter((field): field is string => typeof field === 'string' && field.trim().length > 0);
  }
  const single = firstNonEmptyString(raw);
  return single ? [single] : [];
}

function getDisplayAxisLabel(chart: ChartResponse, axis: 'x' | 'y', fallbackField?: string): string | undefined {
  const specAxis = axis === 'x' ? (chart.spec as any)?.xAxis : (chart.spec as any)?.yAxis;
  const directLabel = axis === 'x' ? (chart as any).x_axis_label : (chart as any).y_axis_label;
  return firstNonEmptyString(directLabel, specAxis?.name, fallbackField);
}

function isChartNumericValue(value: unknown): boolean {
  if (value == null || value === '') return false;
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'string') {
    const normalized = value.trim().replace(/,/g, '');
    return normalized.length > 0 && Number.isFinite(Number(normalized));
  }
  return false;
}

function toChartNumber(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const parsed = Number(value.trim().replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

function humanizeChartKey(key?: string): string {
  if (!key) return '';
  return key
    .replace(/^chart_\d+_/i, '')
    .replace(/^c\d+_/i, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, l => l.toUpperCase());
}

function getChartNumericFields(rows: Array<Record<string, any>>, xField?: string): string[] {
  if (!rows || rows.length === 0) return [];
  const categoryKeys = new Set([...CHART_CATEGORY_FIELD_NAMES, xField].filter(Boolean) as string[]);
  const keys = Array.from(new Set(rows.flatMap(row => Object.keys(row || {}))));

  return keys.filter((key) => {
    if (categoryKeys.has(key)) return false;
    const values = rows
      .map(row => row?.[key])
      .filter(value => value !== null && value !== undefined && value !== '');
    return values.length > 0 && values.every(isChartNumericValue);
  });
}

function getEffectiveChartYFields(
  chart: ChartResponse,
  rows: Array<Record<string, any>>,
  xField?: string,
  rawChartType = getRawChartType(chart),
): string[] {
  const numericFields = getChartNumericFields(rows, xField);
  const declaredYFields = getChartYFields(chart).filter((field) => numericFields.includes(field));
  if (declaredYFields.length > 0) return declaredYFields;
  const yField = getChartYField(chart);
  const isMultiSeriesIntent = rawChartType.includes('multi') || numericFields.length > 1;

  if (isMultiSeriesIntent && numericFields.length > 0) return numericFields;
  if (yField && numericFields.includes(yField)) return [yField];
  if (numericFields.length > 0) return [numericFields[0]];
  return yField ? [yField] : ['value'];
}

function getChartCategoryValue(row: Record<string, any>, xField?: string, yFields: string[] = []): string {
  let value: unknown;
  if (xField && row[xField] !== undefined && row[xField] !== null) {
    value = row[xField];
  } else {
    const excluded = new Set(['value', 'amount', 'count', ...yFields].filter(Boolean));
    const fallbackKey = ['name', 'label', 'date', 'category', 'x', 'dimension'].find(key => row[key] !== undefined && row[key] !== null)
      || Object.keys(row).find(key =>
        !excluded.has(key) &&
        !isChartNumericValue(row[key]) &&
        row[key] !== null &&
        row[key] !== undefined
      );
    value = fallbackKey ? row[fallbackKey] : undefined;
  }

  const label = value === null || value === undefined ? '' : String(value).trim();
  return label || 'Unspecified';
}

function normalizeChartRows(
  rows: Array<Record<string, any>>,
  xField: string | undefined,
  yFields: string[],
  isMultiSeriesChart: boolean,
): any[] {
  return rows.map((item) => {
    const name = getChartCategoryValue(item, xField, yFields);

    if (isMultiSeriesChart) {
      const result: any = { name };
      yFields.forEach((field) => {
        result[field] = toChartNumber(item[field]);
      });
      return result;
    }

    const valueField = yFields[0];
    const fallbackValue = item.value ?? item.amount ?? item.count ?? Object.values(item).find(isChartNumericValue);
    const result: any = {
      name,
      value: toChartNumber(valueField && item[valueField] !== undefined ? item[valueField] : fallbackValue),
    };

    Object.keys(item).forEach((key) => {
      if (key !== xField && !isChartNumericValue(item[key])) {
        result[key] = item[key];
      }
    });

    return result;
  });
}

function buildChartMetricDisplayNames(
  chart: ChartResponse,
  xField: string | undefined,
  yFields: string[],
): Record<string, string> | undefined {
  const displayNames: Record<string, string> = {};
  const backendNames = (chart as any).metric_display_names;

  if (backendNames && typeof backendNames === 'object') {
    Object.entries(backendNames).forEach(([key, value]) => {
      if (typeof value === 'string' && value.trim()) displayNames[key] = value;
    });
  }

  const xLabel = xField
    ? friendlyChartFieldLabel(xField, displayNames, getDisplayAxisLabel(chart, 'x'))
    : getDisplayAxisLabel(chart, 'x');
  if (xField && xLabel) displayNames[xField] = xLabel;
  if (xLabel) {
    displayNames.name = xLabel;
    displayNames.Name = xLabel;
  }

  yFields.forEach((field) => {
    if (!field || displayNames[field]) return;
    const matchingBackendLabel = Object.values(displayNames).find((value) => value === field);
    displayNames[field] = matchingBackendLabel || friendlyChartFieldLabel(field, displayNames);
  });

  const aggregationSources = [
    (chart as any).aggregations,
    (chart as any).operational_plan?.aggregations,
  ].filter(Boolean);

  aggregationSources.forEach((aggregations) => {
    Object.entries(aggregations).forEach(([key, aggregation]: [string, any]) => {
      const label = displayNames[key] || humanizeChartKey(key);
      const column = String(aggregation?.column || '').split('.').pop();
      yFields.forEach((field) => {
        if (field === column && !displayNames[field]) displayNames[field] = label;
      });
    });
  });

  return Object.keys(displayNames).length > 0 ? displayNames : undefined;
}

/**
 * Transform backend charts to frontend ChartData format
 */
function transformCharts(
  charts: ChartResponse[],
  operationPlan?: any,
  sqlPlan?: Record<string, any>,
  sqlQuery?: string,
  selectedTables?: string[],
  sourceData?: Record<string, Array<Record<string, any>>>,
  sourceDataMetadata?: Record<string, {
    total_rows: number;
    preview_rows: number;
    is_truncated: boolean;
    columns: string[];
    dtypes: Record<string, string>;
    query_id: string;
    table_name: string;
    download_available: boolean;
    download_message?: string;
  }>,
): ChartData[] {
  return charts.map((chart, index) => {
    // Determine chart type from backend chart_type (supports all use-case mappings)
    let type: ChartData['type'] = 'bar';

    const chartType = getRawChartType(chart);
    const displayChartType = firstNonEmptyString((chart.spec as any)?.chartType, chart.chart_type) || chart.chart_type;
    const sourceRows = Array.isArray(chart.data) ? chart.data : [];
    const xField = getChartXField(chart);
    const yFields = getEffectiveChartYFields(chart, sourceRows, xField, chartType);
    const isMultiSeriesChart = chartType.includes('multi') || yFields.length > 1;
    const xAxisLabel = getDisplayAxisLabel(chart, 'x', xField);
    const yAxisLabel = getDisplayAxisLabel(chart, 'y', yFields.length === 1 ? yFields[0] : undefined);

    // More specific matches first
    if (chartType.includes('scatter')) type = 'scatter';
    else if (chartType.includes('bubble')) type = 'bubble';
    // Combo (bar + line on dual axes) must be detected before generic
    // dual_axis so the FE picks DualAxisLineChart (combo path), not the
    // line-only fallback. ``bar_line`` covers any future spelling.
    else if (chartType.includes('dual_axis_bar_line') || chartType.includes('bar_line')) type = 'dual_axis_bar_line';
    else if (chartType.includes('dual_axis') || chartType.includes('dual_axis_line')) type = 'dual_axis_line';
    else if (chartType.includes('radar') || chartType.includes('spider')) type = 'radar';
    else if (chartType.includes('parallel')) type = 'parallel_coordinates';
    else if (chartType.includes('sensitivity_matrix') || chartType.includes('sensitivity')) type = 'sensitivity_matrix';
    else if (chartType.includes('correlation_matrix') || chartType.includes('correlation')) type = 'correlation_matrix';
    else if (chartType.includes('heatmap')) type = 'heatmap';
    else if (chartType.includes('treemap')) type = 'treemap';
    else if (chartType.includes('sunburst')) type = 'sunburst';
    else if (chartType.includes('funnel')) type = 'funnel';
    else if (chartType.includes('kpi_sparkline') || chartType.includes('sparkline')) type = 'kpi_sparkline';
    // Legacy chart types removed from the LLM enum (bullet, gauge,
    // candlestick, histogram, boxplot). Map persisted-response values
    // to the closest surviving renderer so old threads still display
    // something rather than crashing.
    else if (chartType.includes('bullet')) type = 'kpi_sparkline';
    else if (chartType.includes('gauge') || chartType.includes('kpi')) type = 'kpi_sparkline';
    else if (chartType.includes('candlestick') || chartType.includes('kline')) type = 'line';
    else if (chartType.includes('waterfall')) type = 'waterfall';
    else if (chartType.includes('histogram')) type = 'bar';
    else if (chartType.includes('box')) type = 'bar';
    else if (chartType.includes('area')) type = 'area';
    else if (chartType.includes('donut')) type = 'donut';
    else if (chartType.includes('pie') || chartType.includes('circular')) type = 'pie';
    else if (chartType.includes('line') || chartType.includes('time')) type = 'line';
    else if (chartType.includes('multi_column') || chartType.includes('multi_column_bar')) type = 'multi_column_bar';
    else if (chartType.includes('stacked_bar')) type = 'stacked_bar';
    else if (chartType.includes('horizontal_bar')) type = 'horizontal_bar';
    else if (chartType.includes('diverging_bar')) type = 'diverging_bar';
    else type = 'bar';

    // Transform chart data
    let transformedData: any[] = [];
    const matrixCells = chartCellsFromBlockData(chart as any) || [];

    if (matrixCells.length > 0) {
      transformedData = chartRowsFromBlockData(chart as any);
    } else if (sourceRows.length > 0) {
      // Use only fields present in data[]. Some v2 charts carry backend column
      // names in y_field while data[] already uses display-ready series labels.
      transformedData = normalizeChartRows(sourceRows, xField, yFields, isMultiSeriesChart);
    } else if (chart.spec) {
      // If no data but we have spec, create placeholder data structure
      transformedData = [];
    }

    // Detect if this is a multi-series chart and add metadata (check transformed data)
    // Only consider it multi-series if there are 2+ numeric fields (excluding 'value' and 'name')
    const transformedNumericFields = getChartNumericFields(transformedData, 'name').filter(key => key !== 'value');
    const isMultiSeries = isMultiSeriesChart || transformedNumericFields.length > 1;

    // Client-side chart override: e.g. pie with 40 categories → horizontal_bar; 1 value → gauge
    const dataLimiting = (chart as any).data_limiting || (chart as any).dataLimiting;
    const originalCount = dataLimiting?.original_count != null ? Number(dataLimiting.original_count) : undefined;
    const signals = deriveSignalsFromChart(sourceRows, chartType, isMultiSeries, originalCount);
    const overrideType = getOverrideChartType(chartType, signals);
    if (overrideType) {
      type = overrideType as ChartData['type'];
    }

    // Build drill-down data for older persisted chart payloads. The public
    // ``ChartData.drillDown`` field is gone, so we keep this local.
    let drillDown: Record<string, any> | undefined = undefined;

    const backendAggregationDetails = (chart as any).aggregation_details || (chart as any).aggregationDetails;

    // Check if chart has its own operational_plan (highest priority)
    const chartOperationalPlan = (chart as any).operational_plan;
    const hasChartOperationalPlan = chartOperationalPlan && typeof chartOperationalPlan === 'object';

    if (hasChartOperationalPlan || operationPlan || sqlPlan || sqlQuery || selectedTables || sourceData || backendAggregationDetails || (chart as any).aggregations) {
      // PRIORITY 1: Use chart's own operational_plan (contains all calculation details)
      // PRIORITY 2: Use chart's own aggregations field (from chart_plan - contains chart-specific keys like chart_001_*)
      // PRIORITY 3: Fall back to extracting from global operationPlan by matching chart ID pattern
      const chartId = chart.data_key || `chart-${String(index + 1).padStart(3, '0')}`;
      let chartAggregations: Record<string, any> = {};
      const chartSteps: MetricStep[] = [];

      // First, try to use chart's own operational_plan (most accurate and complete)
      if (hasChartOperationalPlan && chartOperationalPlan.aggregations) {
        chartAggregations = chartOperationalPlan.aggregations;

        // Build steps from calculation_chain (similar to metrics) showing X-axis and Y-axis calculations
        const calculationChain = chartOperationalPlan.calculation_chain || (chart as any).calculation_chain || {};
        const chartMetadata = (chart as any).chartMetadata || {};
        const planXField = xField || chartMetadata.xField;
        const planYField = yFields.length > 1
          ? yFields.join(', ')
          : (yFields[0] || getChartYField(chart) || chartMetadata.yField);

        // Check if this is a chart-type calculation chain (new format from backend)
        if (calculationChain.type === 'chart') {
          // New format: calculation chain has aggregations array
          const chartAggs = calculationChain.aggregations || [];

          // Add X-axis step if available
          if (planXField) {
            chartSteps.push({
              id: `x-axis-${planXField}`,
              label: `X-Axis: ${planXField}`,
              value: '',
              formula: planXField,
              description: `X-axis field: ${planXField}`,
            });
          }

          // Add steps for each aggregation (Y-axis calculations)
          chartAggs.forEach((agg: any, idx: number) => {
            const aggLabel = agg.key ? agg.key.replace(/^chart_\d+_/i, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : `Aggregation ${idx + 1}`;
            chartSteps.push({
              id: `y-axis-${agg.key || idx}`,
              label: planYField && agg.column?.includes(planYField) ? `Y-Axis: ${planYField}` : aggLabel,
              value: '',
              formula: agg.formula || `${agg.type}(${agg.column})`,
              description: agg.formula || `${agg.type} on ${agg.column}${agg.group_by ? ` grouped by ${agg.group_by}` : ''}`,
            });
          });
        } else {
          // Legacy format: try to build from aggregations directly
          // Helper function to build steps from calculation chain (for derived metrics in charts)
          const buildStepsFromChain = (chain: any, metricName: string, isAxis: 'x' | 'y' | null = null): MetricStep => {
            const axisLabel = isAxis === 'x' ? 'X-Axis' : isAxis === 'y' ? 'Y-Axis' : '';
            const displayName = axisLabel ? `${axisLabel}: ${metricName}` : metricName;

            if (chain.type === 'direct') {
              return {
                id: `step-${metricName}-${isAxis || 'calc'}`,
                label: displayName,
                value: '',
                formula: chain.formula || chain.description || '',
                description: chain.description || chain.formula || `Direct calculation from ${chain.column || 'data'}`,
              };
            } else if (chain.type === 'derived') {
              const subSteps: MetricStep[] = [];
              const dependencies = chain.dependencies || [];
              const dependencyChains = chain.dependency_chains || [];

              if (dependencyChains.length > 0) {
                dependencyChains.forEach((depChainItem: any) => {
                  const depMetric = depChainItem.metric;
                  const depChain = depChainItem.chain;
                  subSteps.push(buildStepsFromChain(depChain, depMetric));
                });
              } else {
                dependencies.forEach((dep: string) => {
                  subSteps.push({
                    id: `dependency-${dep}`,
                    label: dep.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                    value: '',
                    formula: dep,
                    description: `Source metric: ${dep}`,
                  });
                });
              }

              return {
                id: `step-${metricName}-${isAxis || 'calc'}`,
                label: displayName,
                value: '',
                formula: chain.formula || '',
                description: chain.description || `Calculated from: ${dependencies.join(' and ')}`,
                subSteps: subSteps.length > 0 ? subSteps : undefined,
              };
            } else {
              return {
                id: `step-${metricName}-${isAxis || 'calc'}`,
                label: displayName,
                value: '',
                formula: chain.formula || metricName,
                description: chain.description || `Calculation for ${metricName}`,
              };
            }
          };

          // Build X-axis calculation step
          if (planXField && chartAggregations) {
            // Find aggregation that uses xField as group_by
            const xAxisAgg = Object.entries(chartAggregations).find(([_, agg]: [string, any]) => {
              const groupBy = agg.group_by || '';
              return groupBy.includes(planXField) || groupBy === planXField;
            });

            if (xAxisAgg) {
              const [aggKey, agg] = xAxisAgg;
              chartSteps.push({
                id: `x-axis-${planXField}`,
                label: `X-Axis: ${planXField}`,
                value: '',
                formula: agg.group_by ? `GROUP BY ${agg.group_by}` : planXField,
                description: `X-axis field: ${planXField}${agg.group_by ? ` (grouped by ${agg.group_by})` : ''}`,
              });
            } else {
              // X-axis is just a field, not an aggregation
              chartSteps.push({
                id: `x-axis-${planXField}`,
                label: `X-Axis: ${planXField}`,
                value: '',
                formula: planXField,
                description: `X-axis field: ${planXField}`,
              });
            }
          }

          // Build Y-axis calculation steps (one for each aggregation)
          Object.entries(chartAggregations).forEach(([aggKey, agg]: [string, any]) => {
            const column = agg.column || '';
            const aggType = agg.agg || 'sum';
            const groupBy = agg.group_by || '';

            // Determine if this is the main Y-axis
            const isMainYAxis = yFields.some(field => column.includes(field) || column.endsWith(field));

            const formula = groupBy
              ? `${aggType.toUpperCase()}(${column}) GROUP BY ${groupBy}`
              : `${aggType.toUpperCase()}(${column})`;

            chartSteps.push({
              id: isMainYAxis ? `y-axis-${planYField}` : aggKey,
              label: isMainYAxis ? `Y-Axis: ${planYField}` : aggKey.replace(/^chart_\d+_/i, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              value: '',
              description: `${aggType.toUpperCase()} on ${column}${groupBy ? ` grouped by ${groupBy}` : ''}`,
              formula: formula,
            });
          });
        }
      }
      // Second, try to use chart's own aggregations field (fallback)
      else {
        const chartOwnAggregations = (chart as any).aggregations;
        if (chartOwnAggregations && typeof chartOwnAggregations === 'object' && Object.keys(chartOwnAggregations).length > 0) {
          chartAggregations = chartOwnAggregations;
        }

        // Build steps from chart's own aggregations (fallback)
        Object.entries(chartAggregations).forEach(([aggKey, agg]: [string, any]) => {
          const column = agg.column || '';
          const aggType = agg.agg || 'sum';
          const groupBy = agg.group_by || '';

          const formula = groupBy
            ? `${aggType.toUpperCase()}(${column}) GROUP BY ${groupBy}`
            : `${aggType.toUpperCase()}(${column})`;

          chartSteps.push({
            id: aggKey,
            label: aggKey.replace(/^chart_\d+_/i, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
            value: '',
            description: `${aggType.toUpperCase()} on ${column}${groupBy ? ` grouped by ${groupBy}` : ''}`,
            formula: formula,
          });
        });
      }

      // If no chart-specific aggregations found, create a general step from chart metadata
      if (chartSteps.length === 0 && chart.aggregation_type) {
        chartSteps.push({
          id: 'chart-calculation',
          label: chart.title || 'Chart Calculation',
          value: transformedData.length.toString(),
          description: `Chart with ${transformedData.length} data points using ${chart.aggregation_type} aggregation`,
          formula: `${chart.aggregation_type.toUpperCase()}(${getChartYField(chart) || yFields.join(', ') || 'value'})${chart.group_by ? ` GROUP BY ${chart.group_by}` : ''}`,
        });
      }

      // Get source columns for chart
      const sourceColumns: { label: string; value: string }[] = [];
      if (chartAggregations && Object.keys(chartAggregations).length > 0) {
        Object.entries(chartAggregations).forEach(([key, agg]) => {
          if (agg.column) {
            sourceColumns.push({
              label: key.replace(/^chart_\d+_/i, '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              value: agg.column,
            });
          }
          if (agg.group_by) {
            sourceColumns.push({
              label: 'Group By',
              value: agg.group_by,
            });
          }
        });
      }

      // Use operational plan for chart aggregations
      const filteredChartAggs = chartAggregations;

      // Prefer chart-specific table data for drilldown; fallback to computation results or global source_data
      // Check both direct tables_used field AND operational_plan.tables_used (backend sends it directly on chart)
      const tablesUsed = (chart as any).tables_used || (chart as any).operational_plan?.tables_used || [];
      let tableDataForChart: Record<string, Array<Record<string, any>>> = (chart as any).operational_plan?.table_data || {};
      if ((!tableDataForChart || Object.keys(tableDataForChart).length === 0) && sourceData) {
        const filtered: Record<string, Array<Record<string, any>>> = {};
        const tablesToUse = tablesUsed.length > 0 ? tablesUsed : Object.keys(sourceData as any);
        tablesToUse.forEach((t) => {
          const keyExact = (sourceData as any)[t];
          const keyLower = (sourceData as any)[t?.toLowerCase?.() || t];
          const keyUpper = (sourceData as any)[t?.toUpperCase?.() || t];
          const data = keyExact || keyLower || keyUpper;
          if (data) filtered[t] = data;
        });
        tableDataForChart = filtered;
      }
      const excelDataForChart: any = {
        tables: tableDataForChart,
        summary: (chart as any).operational_plan?.computation_results || []
      };
      // Removed debug logging for performance
      const tableDataKeys = Object.keys(tableDataForChart || {});
      const hasChartAggs = !!(chartOperationalPlan && chartOperationalPlan.aggregations && Object.keys(chartOperationalPlan.aggregations).length);
      const drillPlan = (chart as any).operational_plan;
      const drillAggKeys = drillPlan?.aggregations ? Object.keys(drillPlan.aggregations) : [];

      // Prefer top-level chart_response["aggregations"] when present (full spec: column, agg, group_by, filter, reasoning, group_by_reasoning, scenario_role, scenario_multiplier, table, description)
      const aggregationsForDrillDown = (chart as any).aggregations && typeof (chart as any).aggregations === 'object' && Object.keys((chart as any).aggregations).length > 0
        ? (chart as any).aggregations
        : chartAggregations;

      // Only use chart-specific aggregation details, do NOT fall back to global dashboard aggregations
      // This ensures each chart shows only its own aggregations, not all aggregations across all charts
      drillDown = {
        title: `${chart.title} Calculation Details`,
        description: `Complete calculation breakdown for ${chart.title}`,
        steps: chartSteps.length > 0 ? chartSteps : [{
          id: 'chart-data',
          label: chart.title || 'Chart Data',
          value: `${transformedData.length} data points`,
          description: `Chart visualization with ${transformedData.length} aggregated data points`,
        }],
        sourceData: sourceColumns.length > 0 ? sourceColumns : undefined,
        sqlPlan: sqlPlan,
        sqlQuery: sqlQuery,
        // Prefer tables_used from the chart (direct field or operational_plan), normalize to tables present in table_data
        selectedTables: (() => {
          // Check direct tables_used field first, then operational_plan.tables_used, then global selectedTables
          const chartTablesUsed = (chart as any).tables_used || (chart as any).operational_plan?.tables_used || selectedTables;
          if (Array.isArray(chartTablesUsed) && tableDataKeys.length > 0) {
            const normalized = chartTablesUsed.filter((t) =>
              tableDataKeys.includes(t) ||
              tableDataKeys.includes(t.toLowerCase?.() || t) ||
              tableDataKeys.includes(t.toUpperCase?.() || t)
            );
            return normalized.length > 0 ? normalized : tableDataKeys;
          }
          return tableDataKeys.length > 0 ? tableDataKeys : chartTablesUsed;
        })(),
        operationPlan: (chart as any).operational_plan ? { ...(chart as any).operational_plan, aggregations: aggregationsForDrillDown } : {
          aggregations: aggregationsForDrillDown,
          derived: {},
        },
        excelData: excelDataForChart,
        sourceDataMetadata: sourceDataMetadata,  // Pass source data metadata for download feature
        chartMetadata: {
          chartType: displayChartType,
          xField,
          yField: yFields.join(', ') || getChartYField(chart),
          xAxisLabel,
          yAxisLabel,
          groupBy: chart.group_by,
          aggregationType: chart.aggregation_type,
          dataRowCount: chart.data_row_count,
          originalRowCount: chart.original_row_count,
          sortOrder: (chart as any).sort_order,
          filters: (chart as any).filters,
        },
      };
    }

    // Transform full_data if available (same format as regular data)
    let transformedFullData: any[] | undefined = undefined;
    const rawFullData = Array.isArray((chart as any).full_data)
      ? (chart as any).full_data
      : Array.isArray((chart as any).fullData)
        ? (chart as any).fullData
        : undefined;
    if (rawFullData) {
      const fullRows = rawFullData as Array<Record<string, any>>;
      const fullYFields = getEffectiveChartYFields(chart, fullRows, xField, chartType);
      transformedFullData = normalizeChartRows(
        fullRows,
        xField,
        fullYFields,
        chartType.includes('multi') || fullYFields.length > 1 || isMultiSeries,
      );
    }

    // Adjust data for readability limits (category caps, sampling, "Others" grouping)
    const adjustment = adjustChartData(
      type,
      transformedData,
      isMultiSeries,
      transformedFullData,
      (chart as any).data_limiting,
    );
    const metricDisplayNames = buildChartMetricDisplayNames(chart, xField, yFields) || {};
    if (matrixCells.length > 0) {
      const rowDim = firstNonEmptyString((chart as any).matrix_row_dim, (chart as any).matrixRowDim);
      const colDim = firstNonEmptyString((chart as any).matrix_col_dim, (chart as any).matrixColDim, (chart as any).x_field, (chart as any).xField);
      const primaryMeasure = firstNonEmptyString((chart as any).matrix_value_measure, (chart as any).matrixValueMeasure);
      const secondaryMeasure = firstNonEmptyString((chart as any).matrix_secondary_measure, (chart as any).matrixSecondaryMeasure);
      const { rowLabel, colLabel, valueLabel, secondaryLabel } = resolveMatrixDisplayLabels(chart as any, metricDisplayNames);
      metricDisplayNames.Name = rowLabel;
      metricDisplayNames.name = rowLabel;
      metricDisplayNames.Column = colLabel;
      metricDisplayNames.column = colLabel;
      metricDisplayNames.Value = valueLabel;
      metricDisplayNames.value = valueLabel;
      if (secondaryMeasure && secondaryLabel) {
        metricDisplayNames['Secondary Value'] = secondaryLabel;
        metricDisplayNames.secondary_value = secondaryLabel;
      }
      if (rowDim) metricDisplayNames[rowDim] = rowLabel;
      if (colDim) metricDisplayNames[colDim] = colLabel;
      if (primaryMeasure) metricDisplayNames[primaryMeasure] = valueLabel;
      if (secondaryMeasure && secondaryLabel) metricDisplayNames[secondaryMeasure] = secondaryLabel;
    }

    const rawChartTitle = chart.title || `Chart ${index + 1}`;
    const chartTitle = withMatrixPeriodTitle(
      rawChartTitle,
      type,
      chart as any,
      (chart as any).metadata,
      (chart as any).operational_plan,
      (chart as any).operational_plan?.metadata,
    );

    return {
      type,
      title: chartTitle,
      data: adjustment.data,
      category: chart.category || chart.group,
      isMultiSeries: isMultiSeries || chartType.includes('multi'),
      y_field: yFields,
      sort_by: firstNonEmptyString((chart as any).sort_by),
      sort_order: firstNonEmptyString((chart as any).sort_order),
      x_axis_label: xAxisLabel,
      y_axis_label: yAxisLabel,
      metric_display_names: Object.keys(metricDisplayNames).length > 0 ? metricDisplayNames : undefined,
      template: (chart as any).template || (chart as any).config || (chart as any).chart_config,
      dataLimiting: adjustment.dataLimiting ?? dataLimiting,
      fullData: adjustment.fullData?.length ? adjustment.fullData : (transformedFullData?.length ? transformedFullData : transformedData),
      matrix_value_measure: firstNonEmptyString((chart as any).matrix_value_measure, (chart as any).matrixValueMeasure),
      matrix_secondary_measure: firstNonEmptyString((chart as any).matrix_secondary_measure, (chart as any).matrixSecondaryMeasure),
      cells: matrixCells.length > 0 ? matrixCells : undefined,
      reasoning: (chart as any).reasoning,
      // ``drillDown`` is no longer part of the public ChartData type
      // (lineage_v2 replaces it). Attach via spread for older persisted
      // payload consumers; current surfaces ignore it.
      ...(drillDown ? { drillDown } : {}),
    } as ChartData;
  });
}

/**
 * Build formula from operation plan aggregation
 */
function buildFormulaFromAggregation(
  metricName: string,
  operationPlan?: {
    aggregations?: Record<string, any>;
    derived?: Record<string, string>;
  }
): string | undefined {
  if (!operationPlan) return undefined;

  // Check if it's a derived metric
  if (operationPlan.derived && operationPlan.derived[metricName]) {
    return operationPlan.derived[metricName];
  }

  // Check if it's an aggregation
  if (operationPlan.aggregations && operationPlan.aggregations[metricName]) {
    const agg = operationPlan.aggregations[metricName];
    const column = agg.column || '';
    const aggType = agg.agg || '';
    const groupBy = agg.group_by || '';

    if (groupBy) {
      return `${aggType.toUpperCase()}(${column}) GROUP BY ${groupBy}`;
    }
    return `${aggType.toUpperCase()}(${column})`;
  }

  return undefined;
}

/**
 * Get source data columns from operation plan
 */
function getSourceColumns(
  metricName: string,
  operationPlan?: {
    aggregations?: Record<string, any>;
    derived?: Record<string, string>;
  }
): Array<{ label: string; value: string }> {
  const sourceData: Array<{ label: string; value: string }> = [];
  const added = new Set<string>();
  const normalizeMetricName = (name: string) => name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  const formatLabel = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  const addSource = (label: string, value?: string) => {
    if (!value) return;
    const key = `${label}|${value}`;
    if (added.has(key)) return;
    sourceData.push({ label, value });
    added.add(key);
  };

  if (!operationPlan) return sourceData;

  // Check aggregations
  if (operationPlan.aggregations && operationPlan.aggregations[metricName]) {
    const agg = operationPlan.aggregations[metricName];
    const column = agg.column || '';
    const table = agg.table || '';

    if (column) {
      const fullColumn = table ? `${table}.${column}` : column;
      addSource('Source Column', fullColumn);
    }

    if (agg.group_by) {
      addSource('Group By', agg.group_by);
    }
  }

  // Check derived metrics for referenced columns
  if (operationPlan.derived && operationPlan.derived[metricName]) {
    const formula = operationPlan.derived[metricName];
    const resolvedFromAgg: Record<string, { column?: string; groupBy?: string; table?: string }> = {};
    // Extract column references from formula (simple extraction)
    const columnMatches = formula.match(/\b[\w]+\.[\w]+\b/g) || formula.match(/\b[\w]+\b/g);
    if (columnMatches) {
      columnMatches.forEach((col, idx) => {
        if (idx < 5) { // Limit to 5 columns
          addSource(`Formula Reference ${idx + 1}`, col);
        }
      });
    }

    // Also try to map referenced metric tokens to aggregation columns
    if (operationPlan.aggregations) {
      const tokens = (formula.match(/\b[\w]+\b/g) || []).map(normalizeMetricName);
      const referencedAggs: Array<{ key: string; agg: any }> = [];
      Object.entries(operationPlan.aggregations).forEach(([aggKey, agg]) => {
        const normalizedKey = normalizeMetricName(aggKey);
        const isReferenced =
          tokens.includes(normalizedKey) ||
          tokens.some(t => normalizedKey.includes(t) || t.includes(normalizedKey));

        if (isReferenced) {
          referencedAggs.push({ key: aggKey, agg });
          const col = agg.column ? (agg.table ? `${agg.table}.${agg.column}` : agg.column) : undefined;
          resolvedFromAgg[normalizedKey] = { column: col || aggKey, groupBy: agg.group_by, table: agg.table };
          // Prefer real column; fall back to the aggregation key so we still surface something meaningful
          addSource('Referenced Column', col || aggKey);
          if (agg.group_by) addSource('Referenced Group By', agg.group_by);
          if (agg.table && agg.column) addSource('Referenced Table', agg.table);
        }
      });

      // If we added formula references above, replace their values with resolved columns when possible
      sourceData.forEach((item) => {
        const normalizedVal = normalizeMetricName(item.value);
        if (item.label.startsWith('Formula Reference') && resolvedFromAgg[normalizedVal]?.column) {
          item.value = resolvedFromAgg[normalizedVal].column as string;
        }
      });

      // Also surface each referenced aggregation as its own row for clarity
      referencedAggs.forEach(({ key, agg }) => {
        const col = agg.column ? (agg.table ? `${agg.table}.${agg.column}` : agg.column) : undefined;
        addSource(formatLabel(key), col || key);
        if (agg.group_by) addSource(`${formatLabel(key)} Group By`, agg.group_by);
        if (agg.table && agg.column) addSource(`${formatLabel(key)} Table`, agg.table);
      });
    }
  }

  return sourceData;
}

/**
 * Extract KPI metrics from statistics or computation_results with drill-down data
 */
function extractMetrics(
  statistics?: Record<string, any>,
  charts?: ChartResponse[],
  computationResults?: Array<{ metric: string; value: any; status: string; error?: string; operational_plan?: any; category?: string; group?: string }>,
  operationPlan?: {
    aggregations?: Record<string, any>;
    derived?: Record<string, string>;
  },
  sqlPlan?: Record<string, any>,
  sqlQuery?: string,
  selectedTables?: string[],
  sourceDataParam?: Record<string, Array<Record<string, any>>>,
  sourceDataMetadataParam?: Record<string, {
    total_rows: number;
    preview_rows: number;
    is_truncated: boolean;
    columns: string[];
    dtypes: Record<string, string>;
    query_id: string;
    table_name: string;
    download_available: boolean;
    download_message?: string;
  }>
): KPIMetric[] {
  const metrics: KPIMetric[] = [];

  // Helper function to check if value is numeric (not object/dict)
  const isNumericValue = (val: any): boolean => {
    if (val === null || val === undefined) return false;
    if (typeof val === 'object') return false; // Filter out objects and dicts
    if (typeof val === 'number') return !isNaN(val) && isFinite(val);
    if (typeof val === 'string') {
      const parsed = parseFloat(val);
      return !isNaN(parsed) && isFinite(parsed);
    }
    return false;
  };

  // Delegates to the canonical number-formatting policy (utils/numberFormat):
  // abbreviate at Millions and up (2 dp), sub-million shown in full.
  const formatNumericValue = (val: any): string => {
    if (typeof val !== 'number' && (typeof val !== 'string' || isNaN(parseFloat(val)))) {
      return String(val);
    }
    return formatValue(val);
  };

  const normalizeMetricName = (name: string) => {
    return name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
  };

  // Prefer computation_results if available (more detailed)
  if (computationResults && computationResults.length > 0) {
    computationResults.forEach((result, index) => {
      // Only process if status is completed and value is numeric (not object/dict)
      if (result.status === 'completed' && isNumericValue(result.value)) {
        const metricName = result.metric || `Metric ${index + 1}`;
        const value = result.value;
        const formattedValue = formatNumericValue(value);

        // Build formula from result's operational plan or direct aggregation field
        const resultOpPlan = (result as any).operational_plan;
        const directAggregation = (result as any).aggregation;
        const formula = resultOpPlan && resultOpPlan.aggregations ?
          (Object.values(resultOpPlan.aggregations)[0] as any)?.column || 'Direct calculation' :
          directAggregation?.column || 'Direct calculation';

        // Get source columns (metadata) - use operational plan if available, or direct aggregation
        const sourceColumns = resultOpPlan && resultOpPlan.aggregations ?
          [(Object.values(resultOpPlan.aggregations)[0] as any)?.table].filter(Boolean) :
          directAggregation?.table ? [directAggregation.table] : [];

        // Prefer table data from the operational plan (for drilldown tables); fallback to global source_data filtered by tables_used
        // Check both operational_plan.tables_used AND direct tables_used field (backend sends it directly)
        const tablesUsed = (result as any).tables_used || (resultOpPlan as any)?.tables_used || [];
        let tableDataForMetric: Record<string, Array<Record<string, any>>> = resultOpPlan?.table_data || {};
        if ((!tableDataForMetric || Object.keys(tableDataForMetric).length === 0) && sourceDataParam) {
          const filtered: Record<string, Array<Record<string, any>>> = {};
          const tablesToUse = tablesUsed.length > 0 ? tablesUsed : Object.keys(sourceDataParam);
          tablesToUse.forEach((t) => {
            // Case-insensitive matching for table names (consistent with charts)
            const keyExact = sourceDataParam[t];
            const keyLower = sourceDataParam[t?.toLowerCase?.() || t];
            const keyUpper = sourceDataParam[t?.toUpperCase?.() || t];
            const data = keyExact || keyLower || keyUpper;
            if (data) filtered[t] = data;
          });
          tableDataForMetric = filtered;
        }
        const excelDataForMetric: any = { tables: tableDataForMetric, summary: [result] };
        // Removed debug logging for performance

        // Determine icon based on metric name
        const metricLower = metricName.toLowerCase();
        let icon = 'trending-up';
        if (metricLower.includes('cost') || metricLower.includes('expense')) icon = 'dollar';
        else if (metricLower.includes('efficiency') || metricLower.includes('oee')) icon = 'gauge';
        else if (metricLower.includes('production') || metricLower.includes('output')) icon = 'factory';
        else if (metricLower.includes('downtime') || metricLower.includes('maintenance')) icon = 'wrench';

        const metric: KPIMetric = {
          id: `metric-${index}`,
          label: metricName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: formattedValue,
          trend: 'neutral',
          trendValue: '0%',
          icon,
          category: result.category ?? result.group,
          reasoning: (result as any).reasoning,
          column: (result as any).column,
        };

        // Add drill-down if we have operational plan, source data, or SQL details
        const statResultOpPlan = (result as any).operational_plan;

        if (formula || sourceColumns.length > 0 || sqlPlan || sqlQuery || excelDataForMetric || statResultOpPlan) {
          // Use operational plan from the result (includes tables_used information)
          const operationPlan = statResultOpPlan;
          const calculationChain = (result as any).operational_plan?.calculation_chain;

          // Build steps from calculation chain (shows how metric is calculated)
          const steps: MetricStep[] = [];

          if (calculationChain) {
            // Use calculation chain to build hierarchical steps
            if (calculationChain.type === 'direct') {
              // Direct aggregation - show formula directly
              steps.push({
                id: 'main-calculation',
                label: metricName,
                value: formattedValue,
                formula: calculationChain.formula || formula || 'Direct calculation',
                description: calculationChain.description || calculationChain.formula || `Direct calculation from ${calculationChain.column || 'data'}`,
              });
            } else if (calculationChain.type === 'derived') {
              // Derived metric - show "calculated from X and Y" with source metrics
              const dependencies = calculationChain.dependencies || [];
              const dependencyChains = calculationChain.dependency_chains || [];

              // Helper function to recursively build steps from dependency chains
              const buildStepsFromChain = (chain: any, metricName: string, depResult?: any): MetricStep => {
                const value = depResult?.value;
                const formatted = value !== undefined && value !== null
                  ? (typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(value))
                  : 'N/A';

                // Format metric name for display
                const displayName = metricName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                if (chain.type === 'direct') {
                  // Direct aggregation - no sub-steps
                  return {
                    id: `step-${metricName}`,
                    label: displayName,
                    value: formatted,
                    formula: chain.formula || chain.description || '',
                    description: chain.description || chain.formula || `Direct calculation from ${chain.column || 'data'}`,
                  };
                } else if (chain.type === 'derived') {
                  // Derived metric - build sub-steps recursively
                  const subSteps: MetricStep[] = [];
                  const chainDeps = chain.dependencies || [];

                  if (chain.dependency_chains && chain.dependency_chains.length > 0) {
                    chain.dependency_chains.forEach((depChainItem: any, idx: number) => {
                      const depMetric = depChainItem.metric;
                      const depChain = depChainItem.chain;
                      // Try to find dependency result - check both exact match and normalized names
                      const depResult = computationResults?.find((r: any) => {
                        const rMetric = r.metric || '';
                        return rMetric === depMetric ||
                          rMetric.toLowerCase() === depMetric.toLowerCase() ||
                          rMetric.replace(/_/g, '') === depMetric.replace(/_/g, '');
                      });

                      subSteps.push(buildStepsFromChain(depChain, depMetric, depResult));
                    });
                  } else if (chainDeps.length > 0) {
                    // Fallback: use dependencies list
                    chainDeps.forEach((dep: string, idx: number) => {
                      // Try to find dependency result - check both exact match and normalized names
                      const depResult = computationResults?.find((r: any) => {
                        const rMetric = r.metric || '';
                        return rMetric === dep ||
                          rMetric.toLowerCase() === dep.toLowerCase() ||
                          rMetric.replace(/_/g, '') === dep.replace(/_/g, '');
                      });
                      const depChain = depResult?.operational_plan?.calculation_chain;

                      if (depChain) {
                        subSteps.push(buildStepsFromChain(depChain, dep, depResult));
                      } else {
                        const depValue = depResult?.value;
                        const depFormatted = depValue !== undefined && depValue !== null
                          ? (typeof depValue === 'number' ? depValue.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(depValue))
                          : 'N/A';

                        const depDisplayName = dep.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                        subSteps.push({
                          id: `dependency-${dep}-${idx}`,
                          label: depDisplayName,
                          value: depFormatted,
                          formula: dep,
                          description: `Source metric: ${depDisplayName}`,
                        });
                      }
                    });
                  }

                  return {
                    id: `step-${metricName}`,
                    label: displayName,
                    value: formatted,
                    formula: chain.formula || '',
                    description: chain.description || `Calculated from: ${chainDeps.map((d: string) => d.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())).join(' and ')}`,
                    subSteps: subSteps.length > 0 ? subSteps : undefined,
                  };
                } else {
                  // Unknown type
                  return {
                    id: `step-${metricName}`,
                    label: displayName,
                    value: formatted,
                    formula: chain.formula || metricName,
                    description: chain.description || `Calculation for ${displayName}`,
                  };
                }
              };

              // Build sub-steps for dependencies (source metrics)
              const subSteps: MetricStep[] = [];

              if (dependencyChains.length > 0) {
                // Use dependency chains (recursive structure) - these are the source metrics
                dependencyChains.forEach((depChainItem: any) => {
                  const depMetric = depChainItem.metric;
                  const depChain = depChainItem.chain;
                  // Try to find dependency result - check both exact match and normalized names
                  const depResult = computationResults?.find((r: any) => {
                    const rMetric = r.metric || '';
                    return rMetric === depMetric ||
                      rMetric.toLowerCase() === depMetric.toLowerCase() ||
                      rMetric.replace(/_/g, '') === depMetric.replace(/_/g, '');
                  });

                  if (depResult) {
                    subSteps.push(buildStepsFromChain(depChain, depMetric, depResult));
                  } else {
                    // If we can't find the result, still show the metric name
                    const depDisplayName = depMetric.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    subSteps.push({
                      id: `dependency-${depMetric}`,
                      label: depDisplayName,
                      value: 'N/A',
                      formula: depMetric,
                      description: `Source metric: ${depDisplayName}`,
                    });
                  }
                });
              } else if (dependencies.length > 0) {
                // Fallback: use dependencies list - these are the source metrics
                dependencies.forEach((dep: string, idx: number) => {
                  // Try to find dependency result - check both exact match and normalized names
                  const depResult = computationResults?.find((r: any) => {
                    const rMetric = r.metric || '';
                    return rMetric === dep ||
                      rMetric.toLowerCase() === dep.toLowerCase() ||
                      rMetric.replace(/_/g, '') === dep.replace(/_/g, '');
                  });
                  const depChain = depResult?.operational_plan?.calculation_chain;

                  if (depChain) {
                    subSteps.push(buildStepsFromChain(depChain, dep, depResult));
                  } else if (depResult) {
                    // Found result but no chain - show the metric with its value
                    const depValue = depResult.value;
                    const depFormatted = depValue !== undefined && depValue !== null
                      ? (typeof depValue === 'number' ? depValue.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(depValue))
                      : 'N/A';
                    const depDisplayName = dep.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                    subSteps.push({
                      id: `dependency-${dep}-${idx}`,
                      label: depDisplayName,
                      value: depFormatted,
                      formula: dep,
                      description: `Source metric: ${depDisplayName}`,
                    });
                  } else {
                    // Not found - still show it as a source metric
                    const depDisplayName = dep.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                    subSteps.push({
                      id: `dependency-${dep}-${idx}`,
                      label: depDisplayName,
                      value: 'N/A',
                      formula: dep,
                      description: `Source metric: ${depDisplayName}`,
                    });
                  }
                });
              }

              // Format dependencies for display
              const formattedDeps = dependencies.map((d: string) =>
                d.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
              ).join(' and ');

              // Main step showing derived calculation with source metrics as sub-steps
              steps.push({
                id: 'main-calculation',
                label: metricName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                value: formattedValue,
                formula: calculationChain.formula || formula || '',
                description: calculationChain.description || `Calculated from: ${formattedDeps}`,
                subSteps: subSteps.length > 0 ? subSteps : undefined,
              });
            } else {
              // Fallback for unknown type
              steps.push({
                id: 'main-calculation',
                label: metricName,
                value: formattedValue,
                formula: formula || 'Calculation',
                description: calculationChain.description || `Calculated value from computation engine`,
              });
            }
          } else {
            // Fallback: use formula if calculation chain not available
            // But check if we can find dependencies from operation plan
            const operationPlan = statResultOpPlan;
            const derivedFormula = operationPlan?.derived?.[metricName];
            const derivedDeps = operationPlan?.derived ?
              Object.keys(operationPlan.derived).filter(k => {
                // Check if this metric's formula references other metrics
                const formula = operationPlan.derived[metricName];
                if (!formula) return false;
                // Simple check: does formula contain this metric name?
                return formula.includes(k) && k !== metricName;
              }) : [];

            // If we have a derived formula, try to extract dependencies
            let fallbackDeps: string[] = [];
            if (derivedFormula) {
              // Try to find metrics referenced in the formula
              const metricRefs = derivedFormula.match(/\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g) || [];
              fallbackDeps = metricRefs.filter((ref: string) => {
                // Check if ref is a known metric (exists in computation results or aggregations)
                return computationResults?.some((r: any) => r.metric === ref) ||
                  operationPlan?.aggregations?.[ref] ||
                  operationPlan?.derived?.[ref];
              });
            }

            // Build sub-steps for dependencies if found
            const fallbackSubSteps: MetricStep[] = [];
            if (fallbackDeps.length > 0) {
              fallbackDeps.forEach((dep: string, idx: number) => {
                const depResult = computationResults?.find((r: any) => r.metric === dep);
                const depValue = depResult?.value;
                const depFormatted = depValue !== undefined && depValue !== null
                  ? (typeof depValue === 'number' ? depValue.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(depValue))
                  : 'N/A';
                const depDisplayName = dep.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

                fallbackSubSteps.push({
                  id: `fallback-dep-${dep}-${idx}`,
                  label: depDisplayName,
                  value: depFormatted,
                  formula: dep,
                  description: `Source metric: ${depDisplayName}`,
                });
              });
            }

            const formattedFallbackDeps = fallbackDeps.map((d: string) =>
              d.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            ).join(' and ');

            steps.push({
              id: 'main-calculation',
              label: metricName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
              value: formattedValue,
              formula: formula || derivedFormula || 'Direct calculation',
              description: fallbackDeps.length > 0
                ? `Calculated from: ${formattedFallbackDeps}`
                : `Calculated value from computation engine`,
              subSteps: fallbackSubSteps.length > 0 ? fallbackSubSteps : undefined,
            });
          }

          // Compatibility drillDown for old persisted threads.
          // ``KPIMetric.drillDown`` is no longer on the public type
          // (replaced by lineage_v2).
          (metric as any).drillDown = {
            title: `${metricName} Calculation Details`,
            description: `Complete calculation breakdown for ${metricName}`,
            steps: steps,
            sourceData: sourceColumns.length > 0 ? sourceColumns : undefined,
            sqlPlan: sqlPlan,
            sqlQuery: sqlQuery,
            selectedTables: (result as any).tables_used || (resultOpPlan as any)?.tables_used || Object.keys(tableDataForMetric) || selectedTables,
            operationPlan: (result as any).operational_plan,
            excelData: excelDataForMetric,
            sourceDataMetadata: sourceDataMetadataParam,
          };
        }

        metrics.push(metric);
      }
    });
  }
  // Fallback to statistics
  else if (statistics) {
    Object.entries(statistics).forEach(([key, value], index) => {
      // Only process numeric values (filter out objects and dicts)
      if (isNumericValue(value)) {
        const formattedValue = formatNumericValue(value);

        // For basic statistics, use simplified formula representation
        const formula = `Calculated value for ${key}`;
        const sourceColumns = []; // No detailed source info available for basic statistics

        // Get actual Excel data for this metric's table
        let excelDataForMetric: Record<string, Array<Record<string, any>>> | undefined = undefined;
        // Removed metricAggregationKey - using operational_plan directly

        // For statistics, we don't have detailed operational plan information
        // Use available source data if present
        if (sourceDataParam && Object.keys(sourceDataParam).length > 0) {
          // Use the first available table for Excel data
          const firstTableName = Object.keys(sourceDataParam)[0];
          excelDataForMetric = { [firstTableName]: sourceDataParam[firstTableName] };
        }

        // Only use fallback if we couldn't find the specific table
        if (!excelDataForMetric && sourceDataParam && Object.keys(sourceDataParam).length > 0) {
          // Fallback: show all tables when specific table cannot be found
          excelDataForMetric = sourceDataParam;
        }

        const metric: KPIMetric = {
          id: `metric-${index}`,
          label: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          value: formattedValue,
          trend: 'neutral',
          trendValue: '0%',
          icon: 'trending-up',
        };

        // Add basic drill-down for statistics-based metrics
        if (formula || sourceColumns.length > 0 || sqlPlan || sqlQuery || excelDataForMetric) {
          const actualSelectedTables = Array.isArray(selectedTables) && selectedTables.length > 0
            ? selectedTables
            : undefined;

          // Compatibility drillDown — see note above.
          (metric as any).drillDown = {
            title: `${key} Calculation Details`,
            description: `Calculation breakdown for ${key}`,
            steps: [
              {
                id: 'main-calculation',
                label: key,
                value: formattedValue,
                formula: formula || 'Direct value',
                description: `Calculated value from statistics`,
              },
            ],
            sourceData: sourceColumns.length > 0 ? sourceColumns : undefined,
            sqlPlan: sqlPlan,
            sqlQuery: sqlQuery,
            selectedTables: actualSelectedTables,
            operationPlan: undefined,
            excelData: excelDataForMetric,
            sourceDataMetadata: sourceDataMetadataParam,
          };
        }

        metrics.push(metric);
      }
    });
  }

  // If no metrics from statistics, try to extract from first chart
  if (metrics.length === 0 && charts && charts.length > 0) {
    const firstChart = charts[0];
    if (firstChart.data && firstChart.data.length > 0) {
      // Calculate summary metrics from chart data
      const values = firstChart.data
        .map((item: any) => {
          const val = item.value || item.amount || item.count || 0;
          return typeof val === 'number' ? val : parseFloat(val) || 0;
        })
        .filter((v: number) => !isNaN(v));

      if (values.length > 0) {
        const sum = values.reduce((a: number, b: number) => a + b, 0);
        const avg = sum / values.length;
        const max = Math.max(...values);
        const min = Math.min(...values);

        metrics.push(
          {
            id: 'metric-total',
            label: 'Total',
            value: sum.toLocaleString(),
            trend: 'neutral',
            trendValue: '0%',
            icon: 'calculator',
          },
          {
            id: 'metric-avg',
            label: 'Average',
            value: avg.toLocaleString(undefined, { maximumFractionDigits: 2 }),
            trend: 'neutral',
            trendValue: '0%',
            icon: 'trending-up',
          },
          {
            id: 'metric-max',
            label: 'Maximum',
            value: max.toLocaleString(),
            trend: 'up',
            trendValue: '+0%',
            icon: 'arrow-up',
          },
          {
            id: 'metric-min',
            label: 'Minimum',
            value: min.toLocaleString(),
            trend: 'down',
            trendValue: '-0%',
            icon: 'arrow-down',
          }
        );
      }
    }
  }

  return metrics;
}

/**
 * Generate table data from chart data
 */
function generateTableData(charts: ChartResponse[]): TableRow[] {
  const tableRows: TableRow[] = [];

  if (charts.length > 0 && charts[0].data) {
    charts[0].data.forEach((item: any, index: number) => {
      const date = item.date || item.name || item.label || `Entry ${index + 1}`;
      const metric = charts[0].title || 'Metric';
      const value = (item.value || item.amount || item.count || 0).toString();
      const variance = item.variance || item.change || '0%';
      const notes = item.notes || item.description || '';

      tableRows.push({
        date: date.toString(),
        metric,
        value,
        variance: variance.toString(),
        notes: notes.toString(),
      });
    });
  }

  return tableRows;
}

/**
 * Generate action items from insights
 */
function generateActionItems(insights: string[]): ActionItem[] {
  return insights
    .filter(insight => {
      const lower = insight.toLowerCase();
      return lower.includes('recommend') || lower.includes('suggest') || lower.includes('action') || lower.includes('should');
    })
    .map((insight, index) => {
      const lower = insight.toLowerCase();
      let priority: 'high' | 'medium' | 'low' = 'medium';

      if (lower.includes('urgent') || lower.includes('critical') || lower.includes('immediate')) {
        priority = 'high';
      } else if (lower.includes('consider') || lower.includes('optional')) {
        priority = 'low';
      }

      const parts = insight.split(':');
      const title = parts.length > 1 ? parts[0].trim() : insight.substring(0, 60) + (insight.length > 60 ? '...' : '');
      const description = parts.length > 1 ? parts.slice(1).join(':').trim() : insight;

      return {
        id: `action-${index}`,
        priority,
        title,
        description: description.length > 150 ? description.substring(0, 150) + '...' : description,
      };
    });
}

/**
 * Transform backend DashboardResponse to frontend DashboardData
 */

/** Extract main (all-metrics) summary from full summary_text: the part after "**other**" + newline. */
function extractMainSummaryFromFullText(fullText: string | undefined): string {
  if (!fullText || typeof fullText !== 'string') return '';
  const s = fullText.trim();
  for (const marker of ['**other**\r\n', '**other**\n']) {
    const idx = s.indexOf(marker);
    if (idx !== -1) {
      const after = s.slice(idx + marker.length).trim();
      return after || s;
    }
  }
  return s;
}

function firstNonEmptyText(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const text = value.trim();
    if (text) return text;
  }
  return undefined;
}

function extractGroupSummary(group: any): string {
  return firstNonEmptyText(group?.summary, group?.text, group?.content, group?.normal_text) || '';
}

function extractGroupTitle(group: any): string | undefined {
  return firstNonEmptyText(
    group?.title,
    group?.display_title,
    group?.heading,
    group?.label,
    group?.category_label,
    group?.name
  );
}

function extractGroupDescription(group: any): string | undefined {
  return firstNonEmptyText(group?.description, group?.subtitle, group?.caption, group?.helper_text);
}

function extractDashboardTitle(response: DashboardResponse, analysisSummary: any): string | undefined {
  return firstNonEmptyText(
    (response as any).title,
    (response as any).dashboard_title,
    (response as any).heading,
    analysisSummary?.title,
    analysisSummary?.dashboard_title,
    analysisSummary?.heading
  );
}

function extractDashboardSubtitle(response: DashboardResponse, analysisSummary: any): string | undefined {
  return firstNonEmptyText(
    (response as any).subtitle,
    (response as any).description,
    (response as any).caption,
    analysisSummary?.subtitle,
    analysisSummary?.description
  );
}

export function transformDashboardResponse(response: DashboardResponse): DashboardData {
  const structuredBlocksData = transformStructuredBlocksResponse(response);
  if (structuredBlocksData) return structuredBlocksData;

  const analysisSummary = response.analysis_summary;
  const dashboardTitle = extractDashboardTitle(response, analysisSummary);
  const dashboardSubtitle = extractDashboardSubtitle(response, analysisSummary);
  const summaryText = firstNonEmptyText(
    (response as any).summary,
    (response as any).normal_text,
    analysisSummary?.summary_text,
    extractMainSummaryFromFullText(analysisSummary?.summary_text)
  );

  const metrics = extractMetrics(
    response.statistics,
    response.charts,
    response.computation_results,
    undefined,
    response.sql_plan,
    response.generated_sql,
    response.selected_tables,
    response.source_data,
    response.source_data_metadata
  );
  // Smart Insights renders a visual-first dashboard. Keep narrative insight
  // payloads available in the raw response, but do not promote them into the
  // V2 dashboard surface.
  const insights: AIInsight[] = [];
  const charts = transformCharts(
    response.charts || [],
    undefined,
    response.sql_plan,
    response.generated_sql,
    response.selected_tables,
    response.source_data,
    response.source_data_metadata,
  );
  const tableData = generateTableData(response.charts || []);
  const actionItems: ActionItem[] = [];

  // Enrich chart categories from chart_plan when individual charts lack category
  const chartPlan = response.chart_plan;
  if (chartPlan && typeof chartPlan === 'object') {
    const planEntries = Object.values(chartPlan) as any[];
    charts.forEach((c, i) => {
      if (!c.category && planEntries[i]) {
        const planCat = planEntries[i].category || planEntries[i].group;
        if (planCat) c.category = String(planCat);
      }
    });
  }

  // ── Build category groups for story layout ──
  // Priority 1: Use backend pre-built groups (from summary_ready payload).
  // Priority 2: Build from summaries_by_group + chart/metric category fields.
  const rawGroups = (response as any).groups;
  let groups: Array<{ category: string; title?: string; description?: string; summary: string; metrics: KPIMetric[]; charts: ChartData[] }> | undefined;

  if (Array.isArray(rawGroups) && rawGroups.length > 0) {
    // Backend already grouped charts/metrics per category — transform each group's items
    groups = rawGroups.map((g: any) => {
      const gCharts = transformCharts(
        g.charts || [], undefined,
        response.sql_plan, response.generated_sql, response.selected_tables,
        response.source_data, response.source_data_metadata,
      );
      const gMetrics = extractMetrics(
        undefined, undefined, g.metrics || [],
        undefined, response.sql_plan, response.generated_sql,
        response.selected_tables, response.source_data, response.source_data_metadata,
      );
      return {
        category: normalizeCategory(g.category),
        title: extractGroupTitle(g),
        description: extractGroupDescription(g),
        summary: extractGroupSummary(g),
        metrics: gMetrics,
        charts: gCharts,
      };
    }).filter((g: any) => g.charts.length > 0 || g.metrics.length > 0 || g.summary.length > 0);
  } else {
    const rawSummariesByGroup = analysisSummary?.summaries_by_group;
    const summaryMap = new Map<string, { summary: string; title?: string; description?: string }>();
    if (Array.isArray(rawSummariesByGroup)) {
      rawSummariesByGroup.forEach((s: any) => {
        const cat = normalizeCategory(s.category ?? s.group ?? s.name);
        const text = String(s.text ?? s.summary ?? s.content ?? '').trim();
        if (text) {
          summaryMap.set(cat, {
            summary: text,
            title: extractGroupTitle(s),
            description: extractGroupDescription(s),
          });
        }
      });
    } else if (rawSummariesByGroup && typeof rawSummariesByGroup === 'object') {
      Object.entries(rawSummariesByGroup).forEach(([key, val]) => {
        const text = typeof val === 'string' ? val.trim() : String((val as any)?.text ?? '').trim();
        if (text) {
          summaryMap.set(normalizeCategory(key), {
            summary: text,
            title: typeof val === 'string' ? undefined : extractGroupTitle(val),
            description: typeof val === 'string' ? undefined : extractGroupDescription(val),
          });
        }
      });
    }

    const knownCats = Array.from(summaryMap.keys()).filter(k => k !== 'other');
    if (knownCats.length > 0) {
      const matchCat = (title: string) => knownCats.find(c => title.toLowerCase().includes(c));
      charts.forEach(c => { if (!c.category) c.category = matchCat(c.title) || undefined; });
      metrics.forEach(m => { if (!m.category) m.category = matchCat(m.label) || undefined; });
    }

    const categoriesFromData = new Set<string>();
    charts.forEach(c => categoriesFromData.add(normalizeCategory(c.category)));
    metrics.forEach(m => categoriesFromData.add(normalizeCategory(m.category)));
    summaryMap.forEach((_v, k) => categoriesFromData.add(k));

    const summaryOrder = Array.from(summaryMap.keys());
    const orderedCategories = Array.from(categoriesFromData).sort((a, b) => {
      if (a === 'other') return -1;
      if (b === 'other') return 1;
      const ia = summaryOrder.indexOf(a);
      const ib = summaryOrder.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });

    groups = orderedCategories
      .map(cat => ({
        category: cat,
        title: summaryMap.get(cat)?.title,
        description: summaryMap.get(cat)?.description,
        summary: summaryMap.get(cat)?.summary ?? '',
        metrics: metrics.filter(m => normalizeCategory(m.category) === cat),
        charts: charts.filter(c => normalizeCategory(c.category) === cat),
      }))
      .filter(g => g.charts.length > 0 || g.metrics.length > 0 || g.summary.length > 0);
  }

  return {
    query: response.query,
    title: dashboardTitle,
    subtitle: dashboardSubtitle,
    metrics,
    insights,
    charts,
    tableData,
    actionItems,
    intelligenceAnalysis: undefined,
    botMessageId: (response as any).botMessageId || (response as any).bot_message_id,
    summaryText,
    analysisSummary: analysisSummary as any,
    groups: groups && groups.length > 0 ? groups : undefined,
  };
}
